import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, TrendingUp, Users, Compass, Utensils, CreditCard, UserCheck, Eye, ScanLine, Trash2, Download, Filter, Pencil, Mail, X, Percent, ChefHat, Bus, Plus, Minus, Plane, CalendarClock, Car, CheckCircle2, XCircle, MinusCircle, Settings, AlertTriangle, History, ChevronUp, ChevronDown, UtensilsCrossed, BadgeDollarSign, GripVertical, MessageSquare, Copy, RefreshCw, QrCode, Search, Lock, MapPin, Activity, Printer, FileText } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { api, formatISK, formatDate, formatApiErrorDetail, needsTransfer, isOnSite } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const COMMISSION_PERCENT = 30;
const commissionOf = (base) => Math.round((base || 0) * COMMISSION_PERCENT / 100);

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};
const payBadge = (s) => s === "paid"
  ? <span className="text-emerald-400 text-xs uppercase tracking-[0.1em]">Paid</span>
  : s === "comp"
  ? <span className="text-sky-400 text-xs uppercase tracking-[0.1em]">Comp · Free</span>
  : s === "refunded"
  ? <span className="text-[#C44536] text-xs uppercase tracking-[0.1em]">Refunded</span>
  : <span className="text-yellow-500 text-xs uppercase tracking-[0.1em]">{s === "expired" ? "Expired" : "Unpaid"}</span>;

const linkStatus = (b) => {
  if (!b.admin_created || b.payment_status === "refunded") return null;
  if (b.payment_status === "paid") return { label: "Link paid", cls: "text-emerald-400" };
  let expired = false;
  const dt = b.link_created_at || b.created_at;
  if (dt) { try { expired = (Date.now() - new Date(dt).getTime()) > 24 * 3600 * 1000; } catch (_) {} }
  return expired ? { label: "Link expired", cls: "text-[#C44536]" } : { label: "Link sent", cls: "text-amber-400" };
};

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-[#0C0C0E] border border-white/10 p-6">
      <Icon className="text-gray-500 mb-4" size={20} />
      <p data-testid={testid} className="font-display font-extrabold text-3xl">{value}</p>
      <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mt-2">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ tour_bookings: [], dinner_reservations: [] });
  const [tab, setTab] = useState("tours");
  const [analytics, setAnalytics] = useState(null);
  const [paidOnly, setPaidOnly] = useState(false);
  const [cfg, setCfg] = useState({ dates: [], seatings: [], buggy_slots: [] });
  const [editing, setEditing] = useState(null); // booking being edited
  const [editForm, setEditForm] = useState({ date: "", slot: "", seating: "", journeys: { Fire: 0, Ocean: 0, Earth: 0 }, adults: 0, children: 0 });
  const [addDinner, setAddDinner] = useState(null);
  const [savingAdd, setSavingAdd] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [settings, setSettings] = useState({ test_email: "", operator_email: "", low_seats_threshold: 10 });
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState("");
  const [thresholdForm, setThresholdForm] = useState("10");
  const [capHistory, setCapHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [cars, setCars] = useState([]);
  const [assignments, setAssignments] = useState({}); // booking_id -> car_id
  const [transferPrices, setTransferPrices] = useState({}); // booking_id -> ISK
  const [activityCosts, setActivityCosts] = useState({}); // tour_id -> {transfer, operator} per pax
  const [activityList, setActivityList] = useState([]); // [{id,name}]
  const [showActivityCosts, setShowActivityCosts] = useState(false);
  const [planLoaded, setPlanLoaded] = useState(false);
  const [bookingsLoaded, setBookingsLoaded] = useState(false);

  // Activity partner scanner access
  const [partners, setPartners] = useState([]);
  const [tourOptions, setTourOptions] = useState([]);
  const [showPartners, setShowPartners] = useState(false);
  const [restaurantAccess, setRestaurantAccess] = useState([]);
  const [showRestaurant, setShowRestaurant] = useState(false);
  const [newRestaurantName, setNewRestaurantName] = useState("Restaurant");
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [clientQuery, setClientQuery] = useState("");
  const [showTourCaps, setShowTourCaps] = useState(false);
  const [showBookTour, setShowBookTour] = useState(false);
  const [bookTourForm, setBookTourForm] = useState({ tour_id: "", date: "", adults: 2, children: 0, name: "", email: "", phone: "", price: "", send_email: false });
  const [bookTourResult, setBookTourResult] = useState(null);
  const [bookingTour, setBookingTour] = useState(false);
  const [emailingLink, setEmailingLink] = useState(false);
  const createBookingLink = async () => {
    const f = bookTourForm;
    const price = parseInt(f.price, 10);
    if (!f.tour_id || !f.date || !f.name.trim() || !f.email.trim() || isNaN(price) || price < 0) {
      toast.error("Pick a tour, date, enter the client's name + email and a price"); return;
    }
    setBookingTour(true);
    try {
      const r = await api.post("/admin/bookings/tour", {
        tour_id: f.tour_id, date: f.date, adults: Number(f.adults) || 0, children: Number(f.children) || 0,
        price_total: price,
        customer: { full_name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim() },
        origin_url: window.location.origin,
        send_email: !!f.send_email,
      });
      setBookTourResult(r.data);
      loadBookings();
      toast.success(r.data.email_sent ? `Booking created — link emailed to ${f.email.trim()}` : "Booking created — payment link ready");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create booking"); }
    finally { setBookingTour(false); }
  };
  const emailPaymentLink = async (bookingId, email) => {
    setEmailingLink(true);
    try {
      const r = await api.post(`/admin/bookings/${bookingId}/email-link`);
      if (r.data.email_sent) { toast.success(`Link emailed to ${r.data.to}`); setBookTourResult((cur) => cur ? { ...cur, email_sent: true } : cur); }
      else toast.error("Email could not be sent");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not email link"); }
    finally { setEmailingLink(false); }
  };
  const [tourCaps, setTourCaps] = useState({ caps: [], tours: [], dates: [] });
  const [capForm, setCapForm] = useState({ tour_id: "", date: "", max_pax: "" });
  const loadTourCaps = () => api.get("/admin/tour-capacities").then((r) => setTourCaps(r.data || { caps: [], tours: [], dates: [] })).catch(() => {});
  const saveTourCap = async () => {
    const n = parseInt(capForm.max_pax, 10);
    if (!capForm.tour_id || !capForm.date || isNaN(n) || n < 0) { toast.error("Pick a tour, a date and a valid max pax"); return; }
    try {
      await api.put("/admin/tour-capacities", { tour_id: capForm.tour_id, date: capForm.date, max_pax: n });
      setCapForm({ tour_id: "", date: "", max_pax: "" });
      loadTourCaps();
      toast.success("Tour capacity saved");
    } catch (e) { toast.error("Could not save capacity"); }
  };
  const clearTourCap = async (c) => {
    if (!window.confirm(`Remove the ${c.max_pax}-pax cap on ${c.tour_name} (${c.date})? It becomes unlimited again.`)) return;
    try { await api.put("/admin/tour-capacities", { tour_id: c.tour_id, date: c.date, max_pax: null }); loadTourCaps(); toast.success("Cap removed"); }
    catch (e) { toast.error("Could not remove cap"); }
  };
  const [newPartner, setNewPartner] = useState({ name: "", tour_id: "" });
  const [partnerActivity, setPartnerActivity] = useState({ partners: [], activity: [] });
  const loadPartners = () => api.get("/admin/partners").then((r) => setPartners(r.data.partners || [])).catch(() => {});
  const loadPartnerActivity = () => api.get("/admin/partner-activity").then((r) => setPartnerActivity(r.data || { partners: [], activity: [] })).catch(() => {});
  const partnerLink = (p) => `${window.location.origin}/partner/${p.token}`;
  const createPartner = async () => {
    if (!newPartner.name.trim() || !newPartner.tour_id) { toast.error("Enter a partner name and pick a tour"); return; }
    try {
      await api.post("/admin/partners", { name: newPartner.name.trim(), tour_id: newPartner.tour_id });
      setNewPartner({ name: "", tour_id: "" });
      loadPartners();
      loadPartnerActivity();
      toast.success("Partner access link created");
    } catch (e) { toast.error("Could not create partner"); }
  };
  const rotatePartner = async (p) => {
    if (!window.confirm(`Generate a NEW link for ${p.name}? The old link will stop working.`)) return;
    try { await api.post(`/admin/partners/${p.id}/rotate`); loadPartners(); toast.success("New link generated"); }
    catch (e) { toast.error("Could not rotate link"); }
  };
  const deletePartner = async (p) => {
    if (!window.confirm(`Remove partner ${p.name}? Their link will stop working.`)) return;
    try { await api.delete(`/admin/partners/${p.id}`); loadPartners(); loadPartnerActivity(); toast.success("Partner removed"); }
    catch (e) { toast.error("Could not remove partner"); }
  };
  const copyLink = (p) => {
    const link = partnerLink(p);
    navigator.clipboard?.writeText(link).then(() => toast.success("Link copied")).catch(() => window.prompt("Copy this link:", link));
  };

  const loadRestaurant = () => api.get("/admin/restaurant-access").then((r) => setRestaurantAccess(r.data.access || [])).catch(() => {});
  const restaurantLink = (r) => `${window.location.origin}/restaurant/${r.token}`;
  const loadActivityCosts = () => api.get("/admin/activity-costs").then((r) => { setActivityCosts(r.data.costs || {}); setActivityList(r.data.activities || []); }).catch(() => {});
  const setActivityCost = (tourId, field, value) => setActivityCosts((p) => ({ ...p, [tourId]: { ...(p[tourId] || { transfer: 0, operator: 0 }), [field]: value === "" ? 0 : Number(value) } }));
  const saveActivityCosts = () => api.put("/admin/activity-costs", { costs: activityCosts }).then((r) => setActivityCosts(r.data.costs || {})).catch(() => toast.error("Could not save costs"));
  const createRestaurant = async () => {
    try {
      await api.post("/admin/restaurant-access", { name: (newRestaurantName || "").trim() || "Restaurant" });
      setNewRestaurantName("Restaurant");
      loadRestaurant();
      toast.success("Restaurant access link created");
    } catch (e) { toast.error("Could not create access link"); }
  };
  const rotateRestaurant = async (r) => {
    if (!window.confirm(`Generate a NEW link for ${r.name}? The old link will stop working.`)) return;
    try { await api.post(`/admin/restaurant-access/${r.id}/rotate`); loadRestaurant(); toast.success("New link generated"); }
    catch (e) { toast.error("Could not rotate link"); }
  };
  const deleteRestaurant = async (r) => {
    if (!window.confirm(`Remove ${r.name}? Their link will stop working.`)) return;
    try { await api.delete(`/admin/restaurant-access/${r.id}`); loadRestaurant(); toast.success("Access removed"); }
    catch (e) { toast.error("Could not remove access"); }
  };
  const copyRestaurantLink = (r) => {
    const link = restaurantLink(r);
    navigator.clipboard?.writeText(link).then(() => toast.success("Link copied")).catch(() => window.prompt("Copy this link:", link));
  };

  const loadBookings = () => api.get("/admin/bookings").then((r) => { setData(r.data); setBookingsLoaded(true); }).catch(() => {});
  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});

  const refundCancelledTours = async (count, amount) => {
    if (!window.confirm(`Mark ${count} paid cancelled-tour booking(s) (${formatISK(amount)}) as refunded?\n\nThis removes them from your sales/revenue totals but keeps them visible in Tour Bookings for your records. Do this only after the refunds have actually been issued.`)) return;
    try {
      const r = await api.post("/admin/cancelled-tours/refund");
      await Promise.all([loadBookings(), loadStats(), api.get("/admin/analytics").then((x) => setAnalytics(x.data)).catch(() => {})]);
      toast.success(`${r.data.count} booking(s) marked refunded · ${formatISK(r.data.total_refunded)} removed from sales`);
    } catch (e) { toast.error("Could not update bookings"); }
  };

  const toggleNoShow = async (b) => {
    const next = !b.no_show;
    if (next && !window.confirm(`Mark ${b.customer?.full_name || "this guest"} as a NO-SHOW? This clears any check-in for them.`)) return;
    try {
      await api.post(`/admin/bookings/${b.id}/no-show`, { no_show: next });
      await Promise.all([loadBookings(), loadStats()]);
      toast.success(next ? "Marked as no-show" : "No-show cleared");
    } catch (e) { toast.error("Could not update booking"); }
  };

  const toggleCheckin = async (b) => {
    const next = !b.checked_in;
    try {
      await api.post(`/admin/bookings/${b.id}/checkin`, { checked_in: next });
      await Promise.all([loadBookings(), loadStats()]);
      toast.success(next ? "Marked as used" : "Check-in undone");
    } catch (e) { toast.error("Could not update booking"); }
  };

  const loadCapHistory = () => api.get("/admin/dinner/capacity-history").then((r) => setCapHistory(r.data.history || [])).catch(() => {});
  const [dinnerStock, setDinnerStock] = useState(null);
  const loadDinnerStock = () => api.get("/dinner/stock").then((r) => setDinnerStock(r.data)).catch(() => {});

  useEffect(() => {
    loadStats();
    loadBookings();
    loadCapHistory();
    loadDinnerStock();
    loadTemplates();
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
    loadPartners();
    loadPartnerActivity();
    loadRestaurant();
    loadActivityCosts();
    loadTourCaps();
    api.get("/tours").then((r) => setTourOptions(r.data.tours || [])).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/transfer-plan").then((r) => {
      setCars(r.data.cars || []);
      setAssignments(r.data.assignments || {});
      setTransferPrices(r.data.transfer_prices || {});
      setPlanLoaded(true);
    }).catch(() => setPlanLoaded(true));
    const iv = setInterval(() => { loadStats(); loadPartnerActivity(); }, 15000); // live check-in + partner boarding
    return () => clearInterval(iv);
  }, []);

  // Transfer planner (explicit assignment model): cars + assignments + prices
  const savePlan = (next) => {
    const body = {
      cars: next.cars ?? cars,
      assignments: next.assignments ?? assignments,
      transfer_prices: next.transfer_prices ?? transferPrices,
    };
    if (next.cars) setCars(next.cars);
    if (next.assignments) setAssignments(next.assignments);
    if (next.transfer_prices) setTransferPrices(next.transfer_prices);
    api.put("/admin/transfer-plan", body).catch(() => {});
  };
  const persistPlan = () => api.put("/admin/transfer-plan", { cars, assignments, transfer_prices: transferPrices }).catch(() => {});
  // Drop assignments/prices for bookings that are no longer paid.
  // Guard on bookingsLoaded so we never wipe saved assignments while the
  // bookings list is still loading (paidIds would be empty → false cleanup).
  useEffect(() => {
    if (!planLoaded || !bookingsLoaded) return;
    const paidIds = new Set((data.tour_bookings || []).filter((b) => b.payment_status === "paid").map((b) => b.id));
    const carIds = new Set(cars.map((c) => c.id));
    const cleanAssign = {};
    Object.entries(assignments).forEach(([bid, cid]) => { if (paidIds.has(bid) && carIds.has(cid)) cleanAssign[bid] = cid; });
    if (JSON.stringify(cleanAssign) !== JSON.stringify(assignments)) {
      setAssignments(cleanAssign);
      api.put("/admin/transfer-plan", { cars, assignments: cleanAssign, transfer_prices: transferPrices }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planLoaded, bookingsLoaded, data.tour_bookings, cars]);

  const addCar = (date) => {
    const seats = window.prompt("How many pax does this car take?", "8");
    if (seats === null) return;
    const capacity = Math.max(0, parseInt(seats, 10) || 0);
    const n = cars.filter((c) => c.date === date).length + 1;
    savePlan({ cars: [...cars, { id: `car_${Date.now()}`, label: `Car ${n}`, capacity, cost: 0, date }] });
  };
  const removeCar = (id) => {
    const nextAssign = { ...assignments };
    Object.keys(nextAssign).forEach((bid) => { if (nextAssign[bid] === id) delete nextAssign[bid]; });
    savePlan({ cars: cars.filter((c) => c.id !== id), assignments: nextAssign });
  };
  const updateCar = (id, patch) => setCars((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const assignBooking = (bookingId, carId) => {
    const next = { ...assignments };
    if (carId) {
      const car = cars.find((c) => c.id === carId);
      const booking = (data.tour_bookings || []).find((b) => b.id === bookingId);
      if (car && booking && car.date !== booking.date) {
        toast.error("That guest is on a different day — cars only hold guests from the same day");
        return;
      }
      next[bookingId] = carId;
    } else delete next[bookingId];
    savePlan({ assignments: next });
    toast.success(carId ? "Saved — guest added to car" : "Saved — guest removed from car");
  };
  const [dragBooking, setDragBooking] = useState(null); // {id, date}
  const setTransferPrice = (bookingId, value) => setTransferPrices((p) => ({ ...p, [bookingId]: value }));

  const [editingCap, setEditingCap] = useState(null); // {date, seating}
  const [capValue, setCapValue] = useState("");
  const [savingCap, setSavingCap] = useState(false);
  const saveCapacity = async () => {
    const n = parseInt(capValue, 10);
    if (isNaN(n) || n < 0) { toast.error("Enter a valid capacity"); return; }
    setSavingCap(true);
    try {
      await api.put("/admin/dinner/capacities", { date: editingCap.date, seating: editingCap.seating, capacity: n });
      toast.success("Capacity updated");
      setEditingCap(null);
      loadStats();
      loadCapHistory();
    } catch (e) { toast.error("Could not update capacity"); }
    finally { setSavingCap(false); }
  };

  const saveSettings = async () => {
    try {
      const r = await api.put("/admin/settings", { test_email: settingsForm.trim(), low_seats_threshold: parseInt(thresholdForm, 10) || 0 });
      setSettings(r.data);
      toast.success("Settings saved");
      setShowSettings(false);
    } catch (e) { toast.error("Could not save settings"); }
  };

  const openEdit = (b) => {
    // Journeys: use existing; else map legacy diets (Carnivore→Fire, Pescatarian→Ocean, Vegan→Earth); else default all to Fire
    let journeys = { Fire: b.journeys?.Fire || 0, Ocean: b.journeys?.Ocean || 0, Earth: b.journeys?.Earth || 0 };
    if (b.type === "dinner") {
      const jTotal = journeys.Fire + journeys.Ocean + journeys.Earth;
      if (jTotal !== b.party_size) {
        const d = b.diets || {};
        const dTotal = (d.Vegan || 0) + (d.Pescatarian || 0) + (d.Carnivore || 0);
        if (dTotal === b.party_size) {
          journeys = { Fire: d.Carnivore || 0, Ocean: d.Pescatarian || 0, Earth: d.Vegan || 0 };
        } else if (jTotal === 0) {
          journeys = { Fire: b.party_size || 0, Ocean: 0, Earth: 0 };
        }
      }
    }
    setEditForm({ date: b.date, slot: b.slot || "", seating: b.seating || "", journeys, adults: b.adults || 0, children: b.children || 0 });
    setEditing(b);
  };

  const saveEdit = async () => {
    const j = editForm.journeys;
    if (editing.type === "dinner" && (j.Fire + j.Ocean + j.Earth) !== editing.party_size) {
      return toast.error(`Journeys must total the party size (${editing.party_size})`);
    }
    if ((editing.type === "tour" || editing.type === "buggy") && (editForm.adults + editForm.children) < 1) {
      return toast.error("At least one guest is required");
    }
    setSavingEdit(true);
    try {
      const body = { date: editForm.date };
      if (editing.type === "buggy") body.slot = editForm.slot;
      if (editing.type === "tour") body.slot = (editForm.slot || "").trim();
      if (editing.type === "tour" || editing.type === "buggy") {
        body.adults = editForm.adults;
        body.children = editForm.children;
      }
      if (editing.type === "dinner") {
        body.seating = editForm.seating;
        body.journey_fire = j.Fire;
        body.journey_ocean = j.Ocean;
        body.journey_earth = j.Earth;
      }
      await api.patch(`/admin/bookings/${editing.id}`, body);
      toast.success("Booking updated");
      setEditing(null);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update booking");
    } finally {
      setSavingEdit(false);
    }
  };
  const setEditJourney = (key, delta) => setEditForm((f) => ({ ...f, journeys: { ...f.journeys, [key]: Math.max(0, f.journeys[key] + delta) } }));
  const setEditGuest = (key, delta) => setEditForm((f) => ({ ...f, [key]: Math.max(0, f[key] + delta) }));

  const setAddJourney = (key, delta) => setAddDinner((f) => ({ ...f, journeys: { ...f.journeys, [key]: Math.max(0, f.journeys[key] + delta) } }));
  const saveAddDinner = async () => {
    const j = addDinner.journeys;
    const party = j.Fire + j.Ocean + j.Earth;
    if (!addDinner.full_name.trim()) return toast.error("Enter the guest's name");
    if (party < 1) return toast.error("Assign at least one guest to a journey");
    if (addDinner.beverage_count > party) return toast.error("Pairings can't exceed guests");
    const mode = addDinner.price_mode || "standard";
    let custom_total;
    if (mode === "comp") custom_total = 0;
    else if (mode === "custom") {
      custom_total = parseInt(addDinner.custom_total, 10);
      if (isNaN(custom_total) || custom_total < 0) return toast.error("Enter a valid custom price");
    }
    setSavingAdd(true);
    try {
      const res = await api.post("/admin/bookings/manual-dinner", {
        full_name: addDinner.full_name, email: addDinner.email, phone: addDinner.phone,
        date: addDinner.date, seating: addDinner.seating,
        journey_fire: j.Fire, journey_ocean: j.Ocean, journey_earth: j.Earth,
        beverage_count: addDinner.beverage_count,
        send_confirmation: !!addDinner.send_confirmation,
        ...(mode !== "standard" ? { custom_total } : {}),
      });
      const base = mode === "comp" ? "Comp dinner booking added (0 ISK)" : mode === "custom" ? "Dinner booking added (paid, custom price)" : "Dinner booking added (unpaid)";
      if (addDinner.send_confirmation && res.data?.email_sent) toast.success(`${base} · confirmation + QR emailed`);
      else if (addDinner.send_confirmation && res.data?.email_error) toast.warning(`${base} · but email not sent: ${res.data.email_error}`);
      else toast.success(base);
      setAddDinner(null);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add booking");
    } finally {
      setSavingAdd(false);
    }
  };

  const resendEmail = async (b) => {
    const email = (b.customer?.email || "").trim();
    if (!email || !email.includes("@")) {
      toast.error("No email on file for this guest — edit the booking to add one first");
      return;
    }
    if (!window.confirm(`Resend confirmation + QR pass to ${email}?`)) return;
    try {
      const r = await api.post(`/admin/bookings/${b.id}/resend-email`);
      toast.success(`Confirmation + QR sent to ${r.data?.to || email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send email");
    }
  };

  const seatingsForDate = (d) => {
    const extra = (cfg.extra_seatings && cfg.extra_seatings[d]) || [];
    return Array.from(new Set([...(cfg.seatings || []), ...extra])).sort();
  };

  const refreshAll = () => {
    loadBookings();
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  const [deletedSales, setDeletedSales] = useState({ records: [], count: 0, total_amount: 0 });
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedDays, setDeletedDays] = useState(null); // null = all, 7 = last 7 days
  const loadDeletedSales = (days) => {
    const q = days ? `?days=${days}` : "";
    return api.get(`/admin/deleted-sales${q}`).then((r) => setDeletedSales(r.data)).catch(() => {});
  };
  const openDeletedSales = (days) => { setDeletedDays(days); loadDeletedSales(days); setShowDeleted(true); };

  const deleteBooking = async (b) => {
    if (!window.confirm(`Delete this booking for ${b.customer.full_name}? This cannot be undone.`)) return;
    try {
      const r = await api.delete(`/admin/bookings/${b.id}`);
      toast.success(r.data?.archived ? "Booking deleted — kept in Deleted Sales record" : "Booking deleted");
      refreshAll();
      if (showDeleted) loadDeletedSales(deletedDays);
    } catch (e) {
      toast.error("Could not delete booking");
    }
  };

  const refundCancelBooking = async (b) => {
    if (b.payment_status !== "paid") return toast.error("Only a paid booking can be refunded & cancelled");
    const amt = b.amount_charged ?? b.total ?? 0;
    const label = b.tour_name || (b.seating_label ? `${b.seating_label} dinner` : "dinner");
    const emailLine = b.customer?.email ? `\n\nThe guest (${b.customer.email}) will be emailed a "cancelled — refund issued" notice.` : "";
    if (!window.confirm(`Mark ${b.customer?.full_name || "this guest"}'s ${label} booking (${formatISK(amt)}) as REFUNDED & CANCELLED?\n\nDo this only after you've issued the refund in Stripe. It stays visible with a Refunded badge and drops out of your revenue totals.${emailLine}`)) return;
    try {
      const r = await api.post(`/admin/bookings/${b.id}/refund-cancel`);
      toast.success(`Marked refunded & cancelled${r.data?.email_sent ? " · guest emailed" : ""}`);
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark refunded & cancelled");
    }
  };

  const purgeUnpaid = async () => {
    if (!window.confirm("Delete ALL abandoned / unpaid carts (never completed payment)?\n\nPaid, refunded and complimentary bookings are KEPT — they won't be touched. This cannot be undone.")) return;
    try {
      const r = await api.post("/admin/bookings/purge-unpaid");
      toast.success(`${r.data.deleted_count} unpaid cart(s) removed · paid & refunded kept`);
      refreshAll();
    } catch (e) {
      toast.error("Could not purge unpaid bookings");
    }
  };

  const [sendingRebook, setSendingRebook] = useState(false);
  const [rebookResults, setRebookResults] = useState(null);
  const [rebookVariant, setRebookVariant] = useState("thursday");
  const runRebook = async (variant, onlyIds) => {
    setRebookVariant(variant);
    setSendingRebook(true);
    try {
      const endpoint = variant === "friday" ? "/admin/dinner/send-friday-rebook" : "/admin/dinner/send-thursday-rebook";
      const r = await api.post(endpoint, onlyIds ? { only_ids: onlyIds } : {});
      setRebookResults(r.data);
      if (r.data.failed > 0) toast.error(`${r.data.sent} sent · ${r.data.failed} failed — see the list`);
      else toast.success(`Rebooking invite sent to ${r.data.sent} guest(s)${r.data.skipped ? ` · ${r.data.skipped} skipped (no email)` : ""}`);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send rebooking emails");
    } finally {
      setSendingRebook(false);
    }
  };
  const sendThursdayRebook = async () => {
    const thu = (data.dinner_reservations || []).filter((b) => b.date === "2026-10-01");
    if (!window.confirm(`Email all Thursday (1 Oct) dinner guests inviting them to re-choose a Saturday/Sunday sitting?\n\nThis will send to ${thu.length} Thursday reservation(s).`)) return;
    runRebook("thursday", null);
  };
  const sendFridayRebook = async () => {
    const fri = (data.dinner_reservations || []).filter((b) => b.date === "2026-10-02" && b.payment_status === "paid");
    if (!window.confirm(`Cancel Friday (2 Oct) dining and email all PAID Friday guests?\n\nThey can reschedule to an available Sat/Sun time, or request a full refund if nothing suits.\n\nThis will send to ${fri.length} paid Friday reservation(s).`)) return;
    runRebook("friday", null);
  };
  const clearBounce = async (b) => {
    try {
      await api.post(`/admin/bookings/${b.id}/clear-bounce`);
      toast.success("Marked as handled");
      refreshAll();
    } catch (e) { toast.error("Could not update"); }
  };
  const resendFailedRebook = () => {
    const ids = (rebookResults?.results || []).filter((r) => r.status === "failed").map((r) => r.id);
    if (!ids.length) return;
    runRebook(rebookVariant, ids);
  };
  const promptTestEmail = () => {
    const saved = settings.test_email || localStorage.getItem("immersion_test_email") || "";
    const email = window.prompt("Send a test email to which address?", saved);
    if (email && email.trim()) {
      const v = email.trim();
      localStorage.setItem("immersion_test_email", v);
      if (v !== settings.test_email) {
        api.put("/admin/settings", { test_email: v }).then((r) => setSettings(r.data)).catch(() => {});
      }
      return v;
    }
    return null;
  };
  const sendTestRebookEmail = async (variant = "thursday") => {
    const email = promptTestEmail();
    if (!email) return;
    try {
      await api.post("/admin/dinner/rebook-test-email", { email, variant });
      toast.success(`Test email sent to ${email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send test email");
    }
  };

  // ---- Guided tour reschedule (transfer logistics) ----
  const [reschedFor, setReschedFor] = useState(null); // booking being rescheduled
  const [reschedDates, setReschedDates] = useState([]);
  const [reschedNote, setReschedNote] = useState("");
  const [reschedPreview, setReschedPreview] = useState("");
  const [reschedBusy, setReschedBusy] = useState(false);

  const openReschedule = (b) => {
    setReschedFor(b);
    setReschedDates((cfg.dates || []).filter((d) => d !== b.date));
    setReschedNote("");
    setReschedPreview("");
  };
  const toggleReschedDate = (d) => setReschedDates((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);
  const previewReschedule = async () => {
    if (!reschedDates.length) return toast.error("Pick at least one day to offer");
    setReschedBusy(true);
    try {
      const r = await api.post(`/admin/tours/${reschedFor.id}/reschedule-preview`, { dates: reschedDates, note: reschedNote });
      setReschedPreview(r.data.html);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build preview");
    } finally { setReschedBusy(false); }
  };
  const sendReschedule = async () => {
    if (!reschedDates.length) return toast.error("Pick at least one day to offer");
    if (!window.confirm(`Send this reschedule request to ${reschedFor.customer.full_name}?`)) return;
    setReschedBusy(true);
    try {
      await api.post(`/admin/tours/${reschedFor.id}/reschedule-send`, { dates: reschedDates, note: reschedNote });
      toast.success("Reschedule request emailed to the client");
      setReschedFor(null);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send request");
    } finally { setReschedBusy(false); }
  };
  const sendReschedTest = async () => {
    if (!reschedDates.length) return toast.error("Pick at least one day to offer");
    const email = promptTestEmail();
    if (!email) return;
    setReschedBusy(true);
    try {
      await api.post(`/admin/tours/${reschedFor.id}/reschedule-test-email`, { email, dates: reschedDates, note: reschedNote });
      toast.success(`Test email sent to ${email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send test");
    } finally { setReschedBusy(false); }
  };

  // ---- Bulk reschedule: email every guest in a car ----
  const [carReschedFor, setCarReschedFor] = useState(null); // { car, members }
  const [carReschedDates, setCarReschedDates] = useState([]);
  const [carReschedNote, setCarReschedNote] = useState("");
  const [carReschedBusy, setCarReschedBusy] = useState(false);
  const openCarReschedule = (car, members) => {
    setCarReschedFor({ car, members });
    setCarReschedDates((cfg.dates || []).filter((d) => d !== car.date));
    setCarReschedNote("");
  };
  const toggleCarReschedDate = (d) => setCarReschedDates((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);
  const sendCarReschedule = async () => {
    if (!carReschedDates.length) return toast.error("Pick at least one day to offer");
    const ids = carReschedFor.members.map((b) => b.id);
    if (!window.confirm(`Email all ${ids.length} guest(s) in ${carReschedFor.car.label} to reschedule?`)) return;
    setCarReschedBusy(true);
    try {
      const r = await api.post("/admin/cars/reschedule", { booking_ids: ids, dates: carReschedDates, note: carReschedNote });
      toast.success(`Reschedule request sent to ${r.data.sent} guest(s)${r.data.skipped ? ` · ${r.data.skipped} skipped` : ""}`);
      setCarReschedFor(null);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send requests");
    } finally { setCarReschedBusy(false); }
  };

  // ---- Reschedule tracker filter ----
  const [reschedFilter, setReschedFilter] = useState("all"); // all | asked | rescheduled | private
  const matchesResched = (b) => {
    if (reschedFilter === "asked") return b.reschedule_status === "invited";
    if (reschedFilter === "rescheduled") return b.reschedule_status === "rescheduled";
    if (reschedFilter === "private") return !!b.private_transfer_requested;
    return true;
  };

  // ---- Dinner rebook/refund tracker filter ----
  const [dinnerFilter, setDinnerFilter] = useState("all"); // all | invited | rebooked | refund
  const matchesDinner = (b) => {
    if (dinnerFilter === "invited") return b.rebook_status === "invited";
    if (dinnerFilter === "rebooked") return b.rebook_status === "rebooked";
    if (dinnerFilter === "refund") return b.rebook_status === "refund_requested";
    return true;
  };

  // ---- Private transfer quote ----
  const [quoteFor, setQuoteFor] = useState(null);
  const [quoteAmount, setQuoteAmount] = useState("");
  const [quoteNote, setQuoteNote] = useState("");
  const [quoteBusy, setQuoteBusy] = useState(false);
  const openQuote = (b) => { setQuoteFor(b); setQuoteAmount(b.private_transfer_quote ? String(b.private_transfer_quote) : ""); setQuoteNote(b.private_transfer_quote_note || ""); };
  const sendQuote = async () => {
    const amt = Number(quoteAmount);
    if (!amt || amt <= 0) return toast.error("Enter a quote amount in ISK");
    if (!window.confirm(`Email a private transfer quote of ${formatISK(amt)} to ${quoteFor.customer.full_name}?`)) return;
    setQuoteBusy(true);
    try {
      await api.post(`/admin/tours/${quoteFor.id}/private-transfer-quote`, { amount: amt, note: quoteNote });
      toast.success("Private transfer quote emailed to the guest");
      setQuoteFor(null);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send quote");
    } finally { setQuoteBusy(false); }
  };
  const sendQuoteTest = async () => {
    const amt = Number(quoteAmount);
    if (!amt || amt <= 0) return toast.error("Enter a quote amount in ISK");
    const email = promptTestEmail();
    if (!email) return;
    setQuoteBusy(true);
    try {
      await api.post(`/admin/tours/${quoteFor.id}/private-transfer-quote-test`, { email, amount: amt, note: quoteNote });
      toast.success(`Test email sent to ${email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send test");
    } finally { setQuoteBusy(false); }
  };

  // ---- Custom message to an activity guest ----
  const [msgFor, setMsgFor] = useState(null);
  const [msgSubject, setMsgSubject] = useState("");
  const [msgBody, setMsgBody] = useState("");
  const [msgBusy, setMsgBusy] = useState(false);
  const openMessage = (b) => { setMsgFor(b); setMsgSubject(""); setMsgBody(""); };

  // ---- Message templates (reusable presets) ----
  const [templates, setTemplates] = useState([]);
  const loadTemplates = () => api.get("/admin/message-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  const saveTemplate = async (subject, body) => {
    if (!subject.trim() || !body.trim()) return toast.error("Enter a subject and message first");
    const name = window.prompt("Name this template (e.g. Pickup change)");
    if (!name || !name.trim()) return;
    try {
      const r = await api.post("/admin/message-templates", { name: name.trim(), subject, body });
      setTemplates(r.data.templates || []);
      toast.success("Template saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save template"); }
  };
  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      const r = await api.delete(`/admin/message-templates/${id}`);
      setTemplates(r.data.templates || []);
    } catch (e) { toast.error("Could not delete template"); }
  };

  // ---- Bulk message (a whole activity or dinner sitting) ----
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkGroup, setBulkGroup] = useState("");
  const [bulkSubject, setBulkSubject] = useState("");
  const [bulkBody, setBulkBody] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const openBulk = () => { setBulkGroup(""); setBulkSubject(""); setBulkBody(""); setBulkOpen(true); };
  const bulkGroups = () => {
    const isTours = tab === "tours";
    const src = (isTours ? data.tour_bookings : data.dinner_reservations) || [];
    const list = paidOnly ? src.filter((b) => b.payment_status === "paid") : src;
    const map = {};
    list.forEach((b) => {
      const pax = isTours ? ((b.adults || 0) + (b.children || 0)) : (b.party_size || 0);
      if (isTours) {
        const key = `tour|${b.tour_name}|${b.date}`;
        if (!map[key]) map[key] = { key, scope: "tour", tour_name: b.tour_name, date: b.date, label: `${b.tour_name} · ${formatDate(b.date)}`, count: 0, pax: 0 };
        map[key].count += 1; map[key].pax += pax;
        // "All days" group for the whole activity
        const allKey = `tour|${b.tour_name}|ALL`;
        if (!map[allKey]) map[allKey] = { key: allKey, scope: "tour", tour_name: b.tour_name, date: null, label: `${b.tour_name} · All days`, count: 0, pax: 0, allDays: true };
        map[allKey].count += 1; map[allKey].pax += pax;
      } else {
        const key = `dinner|${b.date}|${b.seating}`;
        if (!map[key]) map[key] = { key, scope: "dinner", date: b.date, seating: b.seating, label: `${b.seating_label || "Dinner"} ${b.seating} · ${formatDate(b.date)}`, count: 0, pax: 0 };
        map[key].count += 1; map[key].pax += pax;
      }
    });
    return Object.values(map).sort((a, b) =>
      (a.tour_name || "").localeCompare(b.tour_name || "") ||
      (a.allDays === b.allDays ? 0 : a.allDays ? -1 : 1) ||
      (a.date || "").localeCompare(b.date || "") || a.label.localeCompare(b.label));
  };
  const sendBulk = async () => {
    const g = bulkGroups().find((x) => x.key === bulkGroup);
    if (!g) return toast.error("Pick a group");
    if (!bulkSubject.trim() || !bulkBody.trim()) return toast.error("Enter a subject and message");
    if (!window.confirm(`Email all ${g.count} booking(s) (${g.pax} guest(s)) of "${g.label}"?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/bookings/bulk-message", {
        scope: g.scope, tour_name: g.tour_name || null, date: g.date || null, seating: g.seating || null,
        paid_only: paidOnly, subject: bulkSubject, message: bulkBody,
      });
      toast.success(`Sent to ${r.data.sent} recipient(s)${r.data.skipped ? ` · ${r.data.skipped} skipped` : ""}`);
      setBulkOpen(false);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send bulk message");
    } finally { setBulkBusy(false); }
  };

  const sendMessage = async () => {
    if (!msgSubject.trim()) return toast.error("Enter a subject");
    if (!msgBody.trim()) return toast.error("Enter a message");
    if (!window.confirm(`Email this message to ${msgFor.customer.full_name} (${msgFor.customer.email})?`)) return;
    setMsgBusy(true);
    try {
      await api.post(`/admin/tours/${msgFor.id}/message`, { subject: msgSubject, message: msgBody });
      toast.success("Message emailed to the guest");
      setMsgFor(null);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send message");
    } finally { setMsgBusy(false); }
  };
  const sendMessageTest = async () => {
    if (!msgSubject.trim() || !msgBody.trim()) return toast.error("Enter a subject and message");
    const email = promptTestEmail();
    if (!email) return;
    setMsgBusy(true);
    try {
      await api.post(`/admin/tours/${msgFor.id}/message-test`, { email, subject: msgSubject, message: msgBody });
      toast.success(`Test email sent to ${email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send test");
    } finally { setMsgBusy(false); }
  };


  const orderedItems = (b) => {
    const j = b.journeys || {};
    const parts = ["Fire", "Ocean", "Earth"].filter((k) => j[k]).map((k) => `${j[k]} ${k}`);
    if (b.beverage_count) parts.push(`${b.beverage_count} pairing${b.beverage_count > 1 ? "s" : ""}`);
    return parts.join(", ") || "—";
  };

  const exportCSV = () => {
    const isTours = tab === "tours";
    const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

    if (isTours) {
      const rows = data.tour_bookings;
      const list = paidOnly ? rows.filter((b) => b.payment_status === "paid") : rows;
      const header = ["Ref", "Sold at", "Payment", "Name", "Email", "Phone", "Item", "Date", "Guests", "Amount charged (ISK)", "Commission 30% (ISK)", "Checked in"];
      const lines = list.map((b) => [
        b.id.slice(0, 8).toUpperCase(), b.paid_at || b.created_at || "", b.payment_status || "",
        b.customer.full_name, b.customer.email, b.customer.phone, b.tour_name, b.date,
        b.adults + (b.children || 0), b.amount_charged ?? b.total, commissionOf(b.total), b.checked_in ? "Yes" : "No",
      ].map(q).join(","));
      const csv = [header.map(q).join(","), ...lines].join("\n");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      a.download = "immersion-tours-sales.csv";
      a.click();
      return;
    }

    // Dinner CSV — grouped by day + seating time, guests alphabetical
    const rows = data.dinner_reservations || [];
    const list = paidOnly ? rows.filter((b) => b.payment_status === "paid") : rows;
    const groups = {};
    list.forEach((b) => {
      const key = `${b.date}__${b.seating}`;
      if (!groups[key]) groups[key] = { date: b.date, seating: b.seating, label: b.seating_label || "", items: [] };
      groups[key].items.push(b);
    });
    const header = ["Name", "Party", "Ordered", "Allergies", "Amount charged (ISK)", "Payment", "Email", "Phone", "Ref"];
    const out = [];
    Object.values(groups)
      .sort((a, b) => (a.date || "").localeCompare(b.date || "") || (a.seating || "").localeCompare(b.seating || ""))
      .forEach((g) => {
        out.push(q(`${g.date} — ${`${g.label} ${g.seating}`.trim()}`));
        out.push(header.map(q).join(","));
        [...g.items]
          .sort((a, b) => (a.customer.full_name || "").localeCompare(b.customer.full_name || ""))
          .forEach((b) => {
            out.push([
              b.customer.full_name, b.party_size, orderedItems(b), b.customer.allergies || "",
              Number(b.amount_charged ?? b.total ?? 0).toLocaleString("de-DE", { maximumFractionDigits: 0 }), b.payment_status || "",
              b.customer.email, b.customer.phone, b.id.slice(0, 8).toUpperCase(),
            ].map(q).join(","));
          });
        out.push("");
      });
    const csv = out.join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "immersion-dinners-sales.csv";
    a.click();
  };

  const visible = (rows) => {
    const list = paidOnly ? (rows || []).filter((b) => b.payment_status === "paid") : (rows || []);
    return [...list].sort((a, b) => (a.date || "").localeCompare(b.date || "") || (a.slot || a.seating || "").localeCompare(b.slot || b.seating || ""));
  };

  const downloadCSV = (filename, header, lines) => {
    const csv = [header.join(","), ...lines.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = filename;
    a.click();
  };

  const exportActivityCSV = () => {
    const paid = (data.tour_bookings || []).filter((b) => b.payment_status === "paid" && needsTransfer(b));
    if (paid.length === 0) return toast.error("No paid activities to export");
    const px = (b) => (b.adults || 0) + (b.children || 0);
    const actCost = (b) => {
      const c = activityCosts[b.tour_id] || { transfer: 0, operator: 0 };
      const pax = px(b);
      const t = (c.transfer || 0) * pax, o = (c.operator || 0) * pax;
      return { transfer: t, operator: o, total: t + o };
    };
    const rows = [];
    cars.forEach((car) => {
      const members = paid.filter((b) => assignments[b.id] === car.id);
      let transfers = 0, pax = 0, actTotal = 0;
      members.forEach((b) => {
        const price = Number(transferPrices[b.id]) || 0;
        const ac = actCost(b);
        transfers += price; pax += px(b); actTotal += ac.total;
        rows.push([car.label, b.date, b.tour_name || "Unknown", b.slot || "—", `${b.customer?.full_name || ""} (${px(b)} pax)`, price, ac.transfer, ac.operator, ac.total, "", ""]);
      });
      rows.push([`${car.label} — TOTAL (${pax}/${car.capacity || 0} pax)`, "", "", "", "", transfers, "", "", actTotal, car.cost || 0, transfers - (car.cost || 0)]);
    });
    const unassigned = paid.filter((b) => !assignments[b.id]);
    unassigned.forEach((b) => { const ac = actCost(b); rows.push(["Unassigned", b.date, b.tour_name || "Unknown", b.slot || "—", `${b.customer?.full_name || ""} (${px(b)} pax)`, Number(transferPrices[b.id]) || 0, ac.transfer, ac.operator, ac.total, "", ""]); });
    downloadCSV("immersion-transfer-plan.csv", ["Car", "Date", "Activity", "Time", "Guest", "Transfer Price", "Transfer Cost", "Operator Cost", "Total Activity Cost", "Car Cost", "Car Profit"], rows);
  };

  const exportKitchenCSV = () => {
    const paid = (data.dinner_reservations || []).filter((d) => d.payment_status === "paid");
    if (paid.length === 0) return toast.error("No paid dinners to export");
    const groups = {};
    paid.forEach((d) => {
      const key = `${d.date}__${d.seating}`;
      if (!groups[key]) groups[key] = { date: d.date, seating: d.seating, seating_label: d.seating_label || "", guests: 0, Fire: 0, Ocean: 0, Earth: 0, pairings: 0, allergies: [] };
      const g = groups[key];
      g.guests += d.party_size || 0; g.Fire += d.journeys?.Fire || 0; g.Ocean += d.journeys?.Ocean || 0; g.Earth += d.journeys?.Earth || 0; g.pairings += d.beverage_count || 0;
      const al = (d.customer?.allergies || "").trim();
      if (al) g.allergies.push(`${d.customer?.full_name || "Guest"} (${d.party_size || 1} pax): ${al}`);
    });
    const rows = Object.values(groups).sort((a, b) => a.date.localeCompare(b.date) || a.seating.localeCompare(b.seating)).map((r) => [
      r.date, `${r.seating_label} ${r.seating}`.trim(), r.guests, r.Fire, r.Ocean, r.Earth, r.pairings, r.allergies.join(" | "),
    ]);
    downloadCSV("immersion-kitchen-report.csv", ["Date", "Sitting", "Guests", "Fire", "Ocean", "Earth", "Pairings", "Allergies"], rows);
  };

  const printPrepSheet = () => {
    const paid = (data.dinner_reservations || []).filter((d) => d.payment_status === "paid");
    if (paid.length === 0) return toast.error("No paid dinners to print");
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // group by date -> seating
    const nights = {};
    paid.forEach((d) => {
      const nk = d.date;
      if (!nights[nk]) nights[nk] = {};
      const sk = d.seating;
      if (!nights[nk][sk]) nights[nk][sk] = { seating: d.seating, seating_label: d.seating_label || "", guests: 0, Fire: 0, Ocean: 0, Earth: 0, pairings: 0, allergies: [] };
      const g = nights[nk][sk];
      g.guests += d.party_size || 0; g.Fire += d.journeys?.Fire || 0; g.Ocean += d.journeys?.Ocean || 0; g.Earth += d.journeys?.Earth || 0; g.pairings += d.beverage_count || 0;
      const al = (d.customer?.allergies || "").trim();
      if (al) g.allergies.push({ name: d.customer?.full_name || "Guest", party: d.party_size || 1, text: al });
    });
    let body = "";
    Object.keys(nights).sort().forEach((nk) => {
      const sittings = Object.values(nights[nk]).sort((a, b) => (a.seating || "").localeCompare(b.seating || ""));
      const nTotals = sittings.reduce((t, s) => ({ guests: t.guests + s.guests, Fire: t.Fire + s.Fire, Ocean: t.Ocean + s.Ocean, Earth: t.Earth + s.Earth, pairings: t.pairings + s.pairings }), { guests: 0, Fire: 0, Ocean: 0, Earth: 0, pairings: 0 });
      const allAllergies = sittings.flatMap((s) => s.allergies.map((a) => ({ ...a, seating: `${s.seating_label} ${s.seating}`.trim() })));
      body += `<section class="night"><h2>${esc(formatDate(nk))} <span>${nTotals.guests} guests · ${nTotals.Fire} Fire · ${nTotals.Ocean} Ocean · ${nTotals.Earth} Earth · ${nTotals.pairings} pairings</span></h2>`;
      body += `<table><thead><tr><th>Sitting</th><th>Guests</th><th>Fire</th><th>Ocean</th><th>Earth</th><th>Pairings</th></tr></thead><tbody>`;
      sittings.forEach((s) => {
        body += `<tr><td>${esc(`${s.seating_label} ${s.seating}`.trim())}</td><td>${s.guests}</td><td>${s.Fire}</td><td>${s.Ocean}</td><td>${s.Earth}</td><td>${s.pairings}</td></tr>`;
      });
      body += `</tbody></table>`;
      if (allAllergies.length > 0) {
        body += `<div class="allergy"><h3>⚠ Allergy Alerts</h3><ul>`;
        allAllergies.forEach((a) => { body += `<li><b>${esc(a.name)}</b> <span>(${a.party} pax · ${esc(a.seating)})</span> — ${esc(a.text)}</li>`; });
        body += `</ul></div>`;
      } else {
        body += `<div class="noallergy">No allergies flagged for this night.</div>`;
      }
      body += `</section>`;
    });
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Kitchen Prep Sheet — Immersion Festival</title>
      <style>
        *{box-sizing:border-box} body{font-family:-apple-system,Helvetica,Arial,sans-serif;color:#111;margin:32px;}
        h1{font-size:22px;letter-spacing:1px;text-transform:uppercase;margin:0 0 4px}
        .sub{color:#666;font-size:12px;margin:0 0 24px}
        .night{margin:0 0 30px;page-break-inside:avoid}
        .night h2{font-size:16px;margin:0 0 8px;border-bottom:2px solid #111;padding-bottom:6px}
        .night h2 span{font-weight:400;font-size:12px;color:#666;text-transform:none}
        table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:12px}
        th,td{text-align:left;padding:7px 8px;border-bottom:1px solid #ddd}
        th{font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#888}
        td:not(:first-child),th:not(:first-child){text-align:right}
        .allergy{border:2px solid #c0392b;background:#fdecea;padding:10px 14px;border-radius:4px}
        .allergy h3{color:#c0392b;font-size:12px;text-transform:uppercase;letter-spacing:1px;margin:0 0 8px}
        .allergy ul{margin:0;padding-left:18px}
        .allergy li{font-size:13px;margin-bottom:5px;color:#111}
        .allergy li span{color:#888;font-size:11px}
        .noallergy{color:#888;font-size:12px;font-style:italic}
        @media print{body{margin:12mm}}
      </style></head><body>
      <h1>Kitchen Prep Sheet</h1>
      <p class="sub">Immersion Festival · Iceland · paid dinners · printed ${new Date().toLocaleString()}</p>
      ${body}
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) return toast.error("Please allow pop-ups to print the prep sheet");
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const savePrepSheetPDF = () => {
    const paid = (data.dinner_reservations || []).filter((d) => d.payment_status === "paid");
    if (paid.length === 0) return toast.error("No paid dinners to export");
    const nights = {};
    paid.forEach((d) => {
      const nk = d.date;
      if (!nights[nk]) nights[nk] = {};
      const sk = d.seating;
      if (!nights[nk][sk]) nights[nk][sk] = { seating: d.seating, seating_label: d.seating_label || "", guests: 0, Fire: 0, Ocean: 0, Earth: 0, pairings: 0, allergies: [] };
      const g = nights[nk][sk];
      g.guests += d.party_size || 0; g.Fire += d.journeys?.Fire || 0; g.Ocean += d.journeys?.Ocean || 0; g.Earth += d.journeys?.Earth || 0; g.pairings += d.beverage_count || 0;
      const al = (d.customer?.allergies || "").trim();
      if (al) g.allergies.push({ name: d.customer?.full_name || "Guest", party: d.party_size || 1, text: al });
    });
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 40;
    doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(17, 17, 17);
    doc.text("Kitchen Prep Sheet", margin, 50);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(120, 120, 120);
    doc.text(`Immersion Festival · Iceland · paid dinners · printed ${new Date().toLocaleString()}`, margin, 66);
    let y = 90;
    Object.keys(nights).sort().forEach((nk) => {
      const sittings = Object.values(nights[nk]).sort((a, b) => (a.seating || "").localeCompare(b.seating || ""));
      const t = sittings.reduce((acc, s) => ({ guests: acc.guests + s.guests, Fire: acc.Fire + s.Fire, Ocean: acc.Ocean + s.Ocean, Earth: acc.Earth + s.Earth, pairings: acc.pairings + s.pairings }), { guests: 0, Fire: 0, Ocean: 0, Earth: 0, pairings: 0 });
      if (y > doc.internal.pageSize.getHeight() - 120) { doc.addPage(); y = 50; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(17, 17, 17);
      doc.text(formatDate(nk), margin, y);
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(120, 120, 120);
      doc.text(`${t.guests} guests · ${t.Fire} Fire · ${t.Ocean} Ocean · ${t.Earth} Earth · ${t.pairings} pairings`, margin, y + 14);
      autoTable(doc, {
        startY: y + 24,
        margin: { left: margin, right: margin },
        head: [["Sitting", "Guests", "Fire", "Ocean", "Earth", "Pairings"]],
        body: sittings.map((s) => [`${s.seating_label} ${s.seating}`.trim(), s.guests, s.Fire, s.Ocean, s.Earth, s.pairings]),
        headStyles: { fillColor: [17, 17, 17], textColor: 255, fontSize: 8 },
        bodyStyles: { fontSize: 9, textColor: 30 },
        columnStyles: { 0: { halign: "left" }, 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } },
        theme: "grid",
      });
      y = doc.lastAutoTable.finalY + 14;
      const allAllergies = sittings.flatMap((s) => s.allergies.map((a) => ({ ...a, seating: `${s.seating_label} ${s.seating}`.trim() })));
      if (allAllergies.length > 0) {
        const lines = allAllergies.map((a) => `• ${a.name} (${a.party} pax · ${a.seating}) — ${a.text}`);
        const boxH = 20 + lines.length * 14 + 8;
        if (y + boxH > doc.internal.pageSize.getHeight() - 40) { doc.addPage(); y = 50; }
        doc.setFillColor(253, 236, 234); doc.setDrawColor(192, 57, 43); doc.setLineWidth(1.2);
        doc.rect(margin, y, pageW - margin * 2, boxH, "FD");
        doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(192, 57, 43);
        doc.text("ALLERGY ALERTS", margin + 10, y + 16);
        doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(17, 17, 17);
        lines.forEach((ln, i) => doc.text(ln, margin + 10, y + 32 + i * 14, { maxWidth: pageW - margin * 2 - 20 }));
        y += boxH + 18;
      } else {
        doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(140, 140, 140);
        doc.text("No allergies flagged for this night.", margin, y + 4);
        y += 24;
      }
    });
    doc.save("immersion-kitchen-prep-sheet.pdf");
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background grain">
      <header className="border-b border-white/10 bg-[#050505]/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-display font-extrabold text-xl uppercase tracking-tighter">Immersion Festival</Link>
            <span className="text-[10px] uppercase tracking-[0.3em] text-gray-500 border-l border-white/15 pl-4">Admin</span>
          </div>
          <div className="flex items-center gap-5">
            <button data-testid="open-settings-btn" onClick={() => { setSettingsForm(settings.test_email || ""); setThresholdForm(String(settings.low_seats_threshold ?? 10)); setShowSettings(true); }} className="text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-white inline-flex items-center gap-2 transition-colors">
              <Settings size={15} /> Settings
            </button>
            <button data-testid="admin-logout" onClick={doLogout} className="text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-white inline-flex items-center gap-2 transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      {showSettings && (
        <div data-testid="settings-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowSettings(false)}>
          <div className="bg-[#0C0C0E] border border-white/15 w-full max-w-md p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#c9a24b] mb-1">Settings</p>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl">Test email address</h3>
              </div>
              <button data-testid="settings-close" onClick={() => setShowSettings(false)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>
            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Your email for test sends</label>
            <input data-testid="settings-test-email" type="email" value={settingsForm} onChange={(e) => setSettingsForm(e.target.value)} placeholder="you@immersionfestival.com" className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-2" />
            <p className="text-[11px] text-gray-500 mb-5">Saved to your account and shared across every device you log in from. Used to pre-fill all "Send test to myself" prompts.</p>
            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Low-seats alert threshold</label>
            <input data-testid="settings-low-seats-threshold" type="number" min="0" value={thresholdForm} onChange={(e) => setThresholdForm(e.target.value)} placeholder="10" className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-2" />
            <p className="text-[11px] text-gray-500 mb-5">Any dinner sitting with this many seats left (or fewer) is flagged in the Dining Capacity table.</p>
            <button data-testid="settings-save-btn" onClick={saveSettings} className="w-full uppercase tracking-[0.15em] text-xs font-bold py-3.5 bg-[#c9a24b] text-black hover:bg-[#d9b45f] transition-all inline-flex items-center justify-center gap-2">Save settings</button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between gap-4 mb-10 flex-wrap">
          <h1 className="font-display font-extrabold uppercase tracking-tighter text-4xl">Dashboard</h1>
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/admin/side-event" data-testid="open-side-event-btn" className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <Plane size={16} /> Side Event
            </Link>
            <button data-testid="open-deleted-sales-btn" onClick={() => openDeletedSales(null)} className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <Trash2 size={16} /> Deleted Sales
            </button>
            <Link to="/admin/operations" data-testid="open-operations-btn" className="relative border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <Bus size={16} /> Operations
              {(() => {
                const n = (data.tour_bookings || []).filter((b) => b.payment_status === "paid" && (b.operator_status !== "confirmed" || !b.vehicle_id)).length;
                return n > 0 ? <span data-testid="ops-pending-badge" className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1.5 rounded-full bg-[#C44536] text-white text-[11px] font-bold flex items-center justify-center">{n}</span> : null;
              })()}
            </Link>
            <button data-testid="open-client-search-btn" onClick={() => setShowClientSearch((v) => !v)} className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <Search size={16} /> Find Client
            </button>
            <button data-testid="open-tour-caps-btn" onClick={() => setShowTourCaps((v) => !v)} className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <Users size={16} /> Tour Caps
            </button>
            <button data-testid="open-book-tour-btn" onClick={() => setShowBookTour((v) => !v)} className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <Compass size={16} /> Book Tour
            </button>
            <button data-testid="open-partners-btn" onClick={() => setShowPartners((v) => !v)} className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <QrCode size={16} /> Partner Access
            </button>
            <button data-testid="open-restaurant-btn" onClick={() => setShowRestaurant((v) => !v)} className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <UtensilsCrossed size={16} /> Restaurant Access
            </button>
            <button data-testid="open-activity-costs-btn" onClick={() => setShowActivityCosts((v) => !v)} className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <BadgeDollarSign size={16} /> Activity Costs
            </button>
            <Link to="/admin/marketing" data-testid="open-marketing-qr-btn" className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <QrCode size={16} /> Marketing QR
            </Link>
            <Link to="/admin/activity-log" data-testid="open-activity-log-btn" className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <Activity size={16} /> Activity Log
            </Link>
            <Link to="/admin/refunds" data-testid="open-refunds-btn" className="border border-[#C44536]/40 text-[#e07a6c] hover:border-[#C44536] uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <XCircle size={16} /> Refunds
            </Link>
            <Link to="/admin/dining-sales" data-testid="open-dining-sales-btn" className="border border-amber-300/40 text-amber-200 hover:border-amber-300 uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <Utensils size={16} /> Dining Sales
            </Link>
            <Link to="/admin/tour-sales" data-testid="open-tour-sales-btn" className="border border-[#8CA8B9]/40 text-[#b7cbd6] hover:border-[#8CA8B9] uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <Compass size={16} /> Tour Sales
            </Link>
            <Link to="/admin/scan" data-testid="open-scanner-btn" className="bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-3.5 px-6 transition-colors inline-flex items-center gap-2">
              <ScanLine size={16} /> Scan &amp; Check-in
            </Link>
          </div>
        </div>

        {showBookTour && (
          <div data-testid="book-tour-panel" className="bg-[#0C0C0E] border border-white/10 p-6 md:p-8 mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Compass size={18} className="text-sky-400" />
              <h2 className="text-sm uppercase tracking-[0.2em] text-gray-300">Book a tour for a client (custom price)</h2>
            </div>
            <p className="text-xs text-gray-500 mb-6">Create a booking at any price you like (e.g. a discount) and get a card payment link to send the client. A 4% card fee is added at checkout, like all card payments. The booking confirms automatically once they pay.</p>

            {!bookTourResult ? (
              <>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <select data-testid="book-tour-select" value={bookTourForm.tour_id} onChange={(e) => setBookTourForm((f) => ({ ...f, tour_id: e.target.value }))} className="bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40">
                    <option value="">Pick a tour…</option>
                    {tourOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <select data-testid="book-tour-date" value={bookTourForm.date} onChange={(e) => setBookTourForm((f) => ({ ...f, date: e.target.value }))} className="bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40">
                    <option value="">Pick a day…</option>
                    {(tourCaps.dates || []).map((d) => <option key={d} value={d}>{formatDate(d)}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Adults</label>
                    <input data-testid="book-tour-adults" type="number" min="0" value={bookTourForm.adults} onChange={(e) => setBookTourForm((f) => ({ ...f, adults: e.target.value }))} className="w-full mt-1 bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Children</label>
                    <input data-testid="book-tour-children" type="number" min="0" value={bookTourForm.children} onChange={(e) => setBookTourForm((f) => ({ ...f, children: e.target.value }))} className="w-full mt-1 bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[11px] uppercase tracking-[0.12em] text-gray-500">Price (ISK, before card fee)</label>
                    <input data-testid="book-tour-price" type="number" min="0" value={bookTourForm.price} onChange={(e) => setBookTourForm((f) => ({ ...f, price: e.target.value }))} placeholder="e.g. 88000" className="w-full mt-1 bg-black border border-white/15 px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/40" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3 mb-4">
                  <input data-testid="book-tour-name" value={bookTourForm.name} onChange={(e) => setBookTourForm((f) => ({ ...f, name: e.target.value }))} placeholder="Client full name" className="bg-black border border-white/15 px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/40" />
                  <input data-testid="book-tour-email" type="email" value={bookTourForm.email} onChange={(e) => setBookTourForm((f) => ({ ...f, email: e.target.value }))} placeholder="Client email" className="bg-black border border-white/15 px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/40" />
                  <input data-testid="book-tour-phone" value={bookTourForm.phone} onChange={(e) => setBookTourForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Client phone (optional)" className="bg-black border border-white/15 px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/40" />
                </div>
                {bookTourForm.price && !isNaN(parseInt(bookTourForm.price, 10)) && (
                  <p className="text-xs text-gray-400 mb-4">Client pays <span className="text-white font-medium">{formatISK(Math.round(parseInt(bookTourForm.price, 10) * 1.04))}</span> at checkout <span className="text-gray-600">(incl. 4% card fee)</span></p>
                )}
                <label className="flex items-center gap-2 mb-4 cursor-pointer select-none">
                  <input data-testid="book-tour-send-email" type="checkbox" checked={bookTourForm.send_email} onChange={(e) => setBookTourForm((f) => ({ ...f, send_email: e.target.checked }))} className="w-4 h-4 accent-sky-500" />
                  <span className="text-sm text-gray-300">Email the payment link to the client now</span>
                </label>
                <button data-testid="book-tour-submit" onClick={createBookingLink} disabled={bookingTour} className="bg-white text-black hover:bg-gray-200 disabled:opacity-50 uppercase tracking-[0.15em] text-xs font-bold py-3 px-6 transition-colors inline-flex items-center gap-2">
                  {bookingTour ? <RefreshCw size={14} className="animate-spin" /> : <Compass size={14} />} Create booking &amp; get link
                </button>
              </>
            ) : (
              <div data-testid="book-tour-result" className="border border-emerald-500/30 bg-emerald-950/20 p-5">
                <p className="text-sm text-emerald-300 mb-1">Booking created for <span className="font-medium text-white">{bookTourResult.booking.customer.full_name}</span></p>
                <p className="text-xs text-gray-400 mb-4">{bookTourResult.booking.tour_name} · {formatDate(bookTourResult.booking.date)} · {(bookTourResult.booking.adults || 0) + (bookTourResult.booking.children || 0)} pax · client pays <span className="text-white">{formatISK(bookTourResult.amount_charged)}</span> (base {formatISK(bookTourResult.base_total)} + {formatISK(bookTourResult.card_fee)} card fee)</p>
                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500 mb-2">Payment link — send this to the client</p>
                <div className="flex gap-2 flex-wrap">
                  <input data-testid="book-tour-link" readOnly value={bookTourResult.checkout_url} className="flex-1 min-w-0 bg-black border border-white/15 px-4 py-3 text-xs text-sky-300 font-mono focus:outline-none" />
                  <button data-testid="book-tour-copy" onClick={() => { navigator.clipboard?.writeText(bookTourResult.checkout_url).then(() => toast.success("Link copied")).catch(() => window.prompt("Copy link:", bookTourResult.checkout_url)); }} className="bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center gap-2"><Copy size={14} /> Copy</button>
                  <button data-testid="book-tour-email" onClick={() => emailPaymentLink(bookTourResult.booking.id, bookTourResult.booking.customer.email)} disabled={emailingLink} className="border border-white/20 text-white hover:border-white/60 disabled:opacity-50 uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center gap-2">{emailingLink ? <RefreshCw size={14} className="animate-spin" /> : <Mail size={14} />} Email link</button>
                </div>
                {bookTourResult.email_sent && <p data-testid="book-tour-email-sent" className="text-xs text-emerald-400 mt-2 inline-flex items-center gap-1.5"><CheckCircle2 size={13} /> Payment link emailed to {bookTourResult.booking.customer.email}</p>}
                <p className="text-[11px] text-gray-500 mt-3">This Stripe link is valid for ~24 hours. The booking shows as unpaid until the client completes payment.</p>
                <button data-testid="book-tour-another" onClick={() => { setBookTourResult(null); setBookTourForm({ tour_id: "", date: "", adults: 2, children: 0, name: "", email: "", phone: "", price: "", send_email: false }); }} className="mt-4 text-xs uppercase tracking-[0.12em] text-gray-400 hover:text-white inline-flex items-center gap-2"><Plus size={13} /> Book another</button>
              </div>
            )}
          </div>
        )}

        {showTourCaps && (
          <div data-testid="tour-caps-panel" className="bg-[#0C0C0E] border border-white/10 p-6 md:p-8 mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Users size={18} className="text-sky-400" />
              <h2 className="text-sm uppercase tracking-[0.2em] text-gray-300">Tour capacity limits</h2>
            </div>
            <p className="text-xs text-gray-500 mb-6">Cap how many guests (pax) can book a guided tour on a given day. Once the cap is reached, the public booking page blocks new bookings for that tour/date. Leave a tour uncapped for unlimited.</p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <select data-testid="cap-tour-select" value={capForm.tour_id} onChange={(e) => setCapForm((f) => ({ ...f, tour_id: e.target.value }))} className="flex-1 bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40">
                <option value="">Pick a tour…</option>
                {tourCaps.tours.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <select data-testid="cap-date-select" value={capForm.date} onChange={(e) => setCapForm((f) => ({ ...f, date: e.target.value }))} className="sm:w-56 bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40">
                <option value="">Pick a day…</option>
                {(tourCaps.dates || []).map((d) => <option key={d} value={d}>{formatDate(d)}</option>)}
              </select>
              <input data-testid="cap-max-input" type="number" min="0" value={capForm.max_pax} onChange={(e) => setCapForm((f) => ({ ...f, max_pax: e.target.value }))} placeholder="Max pax" className="sm:w-32 bg-black border border-white/15 px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/40" />
              <button data-testid="cap-save-btn" onClick={saveTourCap} className="bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center justify-center gap-2"><Plus size={14} /> Set cap</button>
            </div>

            {tourCaps.caps.length === 0 ? (
              <p className="text-sm text-gray-500">No capacity limits set — all guided tours are unlimited.</p>
            ) : (
              <div className="border border-white/10 divide-y divide-white/5" data-testid="tour-caps-list">
                {tourCaps.caps.map((c) => (
                  <div key={`${c.tour_id}|${c.date}`} data-testid={`tour-cap-${c.tour_id}-${c.date}`} className="flex items-center gap-3 px-4 py-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm">{c.tour_name}</p>
                      <p className="text-xs text-gray-500">{formatDate(c.date)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-white"><span className={c.remaining === 0 ? "text-[#C44536]" : "text-emerald-400"}>{c.booked_pax}</span> / {c.max_pax} pax</p>
                      <p className="text-[11px] uppercase tracking-[0.1em] text-gray-500">{c.remaining} spot{c.remaining !== 1 ? "s" : ""} left</p>
                    </div>
                    <button data-testid={`cap-remove-${c.tour_id}-${c.date}`} onClick={() => clearTourCap(c)} title="Remove cap (unlimited)" className="text-gray-400 hover:text-red-400 border border-white/10 hover:border-red-500/40 p-2 transition-colors ml-2"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {showClientSearch && (
          <div data-testid="client-search-panel" className="bg-[#0C0C0E] border border-white/10 p-6 md:p-8 mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Search size={18} className="text-sky-400" />
              <h2 className="text-sm uppercase tracking-[0.2em] text-gray-300">Find a client</h2>
            </div>
            <p className="text-xs text-gray-500 mb-5">Type a name or email to see every booking that client has — tours, buggies and dinners.</p>
            <input
              data-testid="client-search-input"
              value={clientQuery}
              onChange={(e) => setClientQuery(e.target.value)}
              autoFocus
              placeholder="Client name or email…"
              className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/40 mb-6"
            />
            {(() => {
              const q = clientQuery.trim().toLowerCase();
              if (q.length < 2) return <p className="text-sm text-gray-500">Start typing at least 2 characters…</p>;
              const all = [
                ...(data.tour_bookings || []).map((b) => ({ ...b, _kind: b.type === "buggy" ? "buggy" : "tour" })),
                ...(data.dinner_reservations || []).map((b) => ({ ...b, _kind: "dinner" })),
              ];
              const matches = all.filter((b) => {
                const n = (b.customer?.full_name || "").toLowerCase();
                const e = (b.customer?.email || "").toLowerCase();
                return n.includes(q) || e.includes(q);
              });
              if (matches.length === 0) return <p data-testid="client-search-empty" className="text-sm text-gray-500">No bookings found for "{clientQuery}".</p>;
              // group by client
              const groups = {};
              matches.forEach((b) => {
                const key = (b.customer?.email || b.customer?.full_name || "—").toLowerCase();
                if (!groups[key]) groups[key] = { name: b.customer?.full_name || "—", email: b.customer?.email || "", phone: b.customer?.phone || "", items: [] };
                groups[key].items.push(b);
              });
              const paxOf = (b) => b._kind === "dinner" ? (b.party_size || 0) : ((b.adults || 0) + (b.children || 0) || 1);
              const badge = (ps) => {
                const map = { paid: "text-emerald-400 border-emerald-500/40", comp: "text-sky-400 border-sky-500/40", unpaid: "text-amber-400 border-amber-500/40", pending: "text-amber-400 border-amber-500/40" };
                return <span className={`text-[10px] uppercase tracking-[0.1em] border px-2 py-0.5 ${map[ps] || "text-gray-400 border-white/20"}`}>{ps}</span>;
              };
              return (
                <div className="space-y-5" data-testid="client-search-results">
                  {Object.entries(groups).map(([key, g]) => (
                    <div key={key} data-testid={`client-group-${key}`} className="border border-white/10">
                      <div className="px-4 py-3 bg-white/[0.03] border-b border-white/10 flex items-baseline justify-between gap-3 flex-wrap">
                        <p className="text-white text-sm font-medium">{g.name}</p>
                        <p className="text-xs text-gray-500">{g.email}{g.phone ? ` · ${g.phone}` : ""} · {g.items.length} booking{g.items.length !== 1 ? "s" : ""}</p>
                      </div>
                      <div className="divide-y divide-white/5">
                        {g.items.sort((a, b) => (a.date || "").localeCompare(b.date || "")).map((b) => (
                          <div key={b.id} data-testid={`client-booking-${b.id}`} className="flex items-center gap-3 px-4 py-3">
                            {b._kind === "dinner" ? <UtensilsCrossed size={15} className="text-amber-400 shrink-0" /> : b._kind === "buggy" ? <Car size={15} className="text-sky-400 shrink-0" /> : <Compass size={15} className="text-sky-400 shrink-0" />}
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-white truncate">{b._kind === "dinner" ? `Dinner · ${b.seating_label || ""} ${b.seating || ""}`.trim() : b.tour_name}</p>
                              <p className="text-xs text-gray-500">{formatDate(b.date)}{b.slot ? ` · ${b.slot}` : ""} · {paxOf(b)} {b._kind === "dinner" ? "guests" : "pax"}{b.checked_in ? " · ✓ boarded" : ""}</p>
                            </div>
                            {badge(b.payment_status)}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {showPartners && (
          <div data-testid="partners-panel" className="bg-[#0C0C0E] border border-white/10 p-6 md:p-8 mb-8">
            <div className="flex items-center gap-3 mb-2">
              <QrCode size={18} className="text-sky-400" />
              <h2 className="text-sm uppercase tracking-[0.2em] text-gray-300">Activity Partner Scanner Access</h2>
            </div>
            <p className="text-xs text-gray-500 mb-6">Give each activity partner a private link. They open it on their phone — no password — and can only scan &amp; tick off guests for their own tour. Unpaid guests are rejected with "Not paid yet".</p>

            {/* Create form */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <input
                data-testid="partner-name-input"
                value={newPartner.name}
                onChange={(e) => setNewPartner((p) => ({ ...p, name: e.target.value }))}
                placeholder="Partner name (e.g. Viking Buggy Co.)"
                className="flex-1 bg-black border border-white/15 px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/40"
              />
              <select
                data-testid="partner-tour-select"
                value={newPartner.tour_id}
                onChange={(e) => setNewPartner((p) => ({ ...p, tour_id: e.target.value }))}
                className="sm:w-64 bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40"
              >
                <option value="">Assign a tour…</option>
                {tourOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button data-testid="partner-create-btn" onClick={createPartner} className="bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center justify-center gap-2"><Plus size={14} /> Create link</button>
            </div>

            {/* Partner list */}
            {partners.length === 0 ? (
              <p className="text-sm text-gray-500">No partners yet. Create one above.</p>
            ) : (
              <div className="border border-white/10 divide-y divide-white/5" data-testid="partners-list">
                {partners.map((p) => (
                  <div key={p.id} data-testid={`partner-item-${p.id}`} className="flex items-center gap-3 px-4 py-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm">{p.name}</p>
                      <p className="text-xs text-gray-500">{p.tour_name}</p>
                      <p className="text-[11px] text-sky-400/80 truncate mt-1 font-mono">{partnerLink(p)}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button data-testid={`partner-copy-${p.id}`} onClick={() => copyLink(p)} title="Copy link" className="text-gray-400 hover:text-white border border-white/10 hover:border-white/40 p-2 transition-colors"><Copy size={14} /></button>
                      <a data-testid={`partner-open-${p.id}`} href={partnerLink(p)} target="_blank" rel="noreferrer" title="Open scanner" className="text-gray-400 hover:text-sky-300 border border-white/10 hover:border-sky-400/40 p-2 transition-colors"><ScanLine size={14} /></a>
                      <button data-testid={`partner-rotate-${p.id}`} onClick={() => rotatePartner(p)} title="Generate a new link" className="text-gray-400 hover:text-amber-300 border border-white/10 hover:border-amber-400/40 p-2 transition-colors"><RefreshCw size={14} /></button>
                      <button data-testid={`partner-delete-${p.id}`} onClick={() => deletePartner(p)} title="Remove partner" className="text-gray-400 hover:text-red-400 border border-white/10 hover:border-red-500/40 p-2 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Live boarding progress per partner */}
            {partnerActivity.partners.length > 0 && (
              <div className="mt-8" data-testid="live-partner-view">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <p className="text-xs uppercase tracking-[0.2em] text-emerald-400/80">Live boarding · updates automatically</p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {partnerActivity.partners.map((p) => {
                    const pct = p.total > 0 ? Math.round((p.boarded / p.total) * 100) : 0;
                    return (
                      <div key={p.id} data-testid={`live-partner-${p.id}`} className="bg-black border border-white/10 p-4">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-sm text-white truncate">{p.name}</p>
                          <p className="font-display font-extrabold text-lg shrink-0"><span data-testid={`live-partner-boarded-${p.id}`} className="text-emerald-400">{p.boarded}</span><span className="text-gray-600">/{p.total}</span></p>
                        </div>
                        <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500 truncate">{p.tour_name}</p>
                        <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 transition-all duration-700" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-[11px] text-gray-500 mt-2">{p.boarded_pax} / {p.total_pax} pax boarded</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recent boarding activity log */}
            <div className="mt-8" data-testid="partner-activity-log">
              <div className="flex items-center gap-2 mb-3">
                <History size={14} className="text-gray-500" />
                <p className="text-xs uppercase tracking-[0.2em] text-gray-400">Activity log · who boarded whom</p>
              </div>
              {partnerActivity.activity.length === 0 ? (
                <p className="text-sm text-gray-500">No boarding activity yet.</p>
              ) : (
                <div className="border border-white/10 divide-y divide-white/5 max-h-80 overflow-y-auto">
                  {partnerActivity.activity.map((a) => (
                    <div key={a.id} data-testid={`activity-${a.id}`} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                      {a.action === "board"
                        ? <CheckCircle2 size={15} className="text-emerald-400 shrink-0" />
                        : a.action === "no_show"
                        ? <XCircle size={15} className="text-[#C44536] shrink-0" />
                        : <MinusCircle size={15} className="text-amber-400 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="text-gray-200 truncate">
                          <span className="text-white font-medium">{a.by_name}</span>
                          {a.action === "board" ? " boarded " : a.action === "no_show" ? " marked no-show " : a.action === "no_show_cleared" ? " cleared no-show for " : " un-boarded "}
                          <span className="text-white">{a.guest_name || "—"}</span>
                          <span className="text-gray-500"> · {a.tour_name}</span>
                        </p>
                      </div>
                      <span className="shrink-0 text-[10px] uppercase tracking-[0.1em] text-gray-500">{a.via === "scan" ? "Scanned" : "Ticked"}</span>
                      <span className="shrink-0 text-xs text-gray-500">{fmtDateTime(a.at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}
        {showActivityCosts && (
          <div data-testid="activity-costs-panel" className="bg-[#0C0C0E] border border-amber-500/20 p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-2">
              <BadgeDollarSign size={18} className="text-amber-400" />
              <h2 className="font-display font-extrabold uppercase tracking-tight text-xl">Activity Costs</h2>
            </div>
            <p className="text-xs text-gray-500 mb-6">Set the transfer cost and operator cost <strong>per guest</strong> for each activity. They're summed into a total per pax and applied everywhere that activity appears — the Transfer Planner, the profit lines and the CSV export.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.12em] text-gray-500">
                    <th className="text-left font-medium py-2 pr-4">Activity</th>
                    <th className="text-right font-medium py-2 px-2">Transfer / pax</th>
                    <th className="text-right font-medium py-2 px-2">Operator / pax</th>
                    <th className="text-right font-medium py-2 pl-2">Total / pax</th>
                  </tr>
                </thead>
                <tbody>
                  {activityList.map((a) => {
                    const c = activityCosts[a.id] || { transfer: 0, operator: 0 };
                    const total = (Number(c.transfer) || 0) + (Number(c.operator) || 0);
                    return (
                      <tr key={a.id} data-testid={`activity-cost-row-${a.id}`} className="border-t border-white/5">
                        <td className="py-2.5 pr-4 text-gray-200 whitespace-nowrap">{a.name}</td>
                        <td className="py-2 px-2 text-right">
                          <input type="number" min="0" data-testid={`activity-transfer-${a.id}`} value={c.transfer || ""} placeholder="0"
                            onChange={(e) => setActivityCost(a.id, "transfer", e.target.value)} onBlur={saveActivityCosts}
                            className="w-28 bg-black border border-white/20 px-2 py-1.5 text-sm text-white text-right focus:outline-none focus:border-amber-400/50" />
                        </td>
                        <td className="py-2 px-2 text-right">
                          <input type="number" min="0" data-testid={`activity-operator-${a.id}`} value={c.operator || ""} placeholder="0"
                            onChange={(e) => setActivityCost(a.id, "operator", e.target.value)} onBlur={saveActivityCosts}
                            className="w-28 bg-black border border-white/20 px-2 py-1.5 text-sm text-white text-right focus:outline-none focus:border-amber-400/50" />
                        </td>
                        <td data-testid={`activity-total-${a.id}`} className="py-2 pl-2 text-right text-amber-300 font-semibold">{total.toLocaleString("en-US")} ISK</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {showRestaurant && (
          <div data-testid="restaurant-panel" className="bg-[#0C0C0E] border border-amber-500/20 p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-2">
              <UtensilsCrossed size={18} className="text-amber-400" />
              <h2 className="font-display font-extrabold uppercase tracking-tight text-xl">Restaurant Access</h2>
            </div>
            <p className="text-xs text-gray-500 mb-6">Give the restaurant a private link to check guests into the dining experience. They open it on their phone — no password, dining only (no tours). They can scan QR codes or tick guests off per seating time, and seat someone from another sitting if needed. Unpaid guests are rejected.</p>
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <input data-testid="restaurant-name-input" value={newRestaurantName} onChange={(e) => setNewRestaurantName(e.target.value)} placeholder="Access name (e.g. Restaurant front desk)"
                className="flex-1 bg-black border border-white/15 px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-400/40" />
              <button data-testid="restaurant-create-btn" onClick={createRestaurant} className="bg-amber-500 text-black hover:bg-amber-400 uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center justify-center gap-2"><Plus size={14} /> Create link</button>
            </div>
            {restaurantAccess.length === 0 ? (
              <p className="text-sm text-gray-500">No restaurant links yet. Create one above.</p>
            ) : (
              <div className="border border-white/10 divide-y divide-white/5" data-testid="restaurant-list">
                {restaurantAccess.map((r) => (
                  <div key={r.id} data-testid={`restaurant-item-${r.id}`} className="flex items-center gap-3 px-4 py-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate inline-flex items-center gap-2"><UtensilsCrossed size={14} className="text-amber-400" /> {r.name}</p>
                      <p className="text-[11px] text-amber-400/80 truncate mt-1 font-mono">{restaurantLink(r)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button data-testid={`restaurant-copy-${r.id}`} onClick={() => copyRestaurantLink(r)} title="Copy link" className="text-gray-400 hover:text-white border border-white/10 hover:border-white/40 p-2 transition-colors"><Copy size={14} /></button>
                      <a data-testid={`restaurant-open-${r.id}`} href={restaurantLink(r)} target="_blank" rel="noreferrer" title="Open check-in" className="text-gray-400 hover:text-amber-300 border border-white/10 hover:border-amber-400/40 p-2 transition-colors"><ScanLine size={14} /></a>
                      <button data-testid={`restaurant-rotate-${r.id}`} onClick={() => rotateRestaurant(r)} title="Generate a new link" className="text-gray-400 hover:text-amber-300 border border-white/10 hover:border-amber-400/40 p-2 transition-colors"><RefreshCw size={14} /></button>
                      <button data-testid={`restaurant-delete-${r.id}`} onClick={() => deleteRestaurant(r)} title="Remove access" className="text-gray-400 hover:text-red-400 border border-white/10 hover:border-red-500/40 p-2 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {stats && (
          <div data-testid="checkin-counter" className="bg-gradient-to-br from-emerald-950/40 to-[#0C0C0E] border border-emerald-500/25 p-6 md:p-8 mb-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full border border-emerald-500/40 flex items-center justify-center">
                  <UserCheck className="text-emerald-400" size={22} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-emerald-400/80 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live Check-in
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Guests arrived · updates automatically</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-display font-extrabold text-4xl leading-none">
                  <span data-testid="checkin-count" className="text-emerald-400">{stats.checked_in_guests}</span>
                  <span className="text-gray-600"> / {stats.total_guests}</span>
                </p>
                <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mt-2">
                  {stats.total_guests > 0 ? Math.round((stats.checked_in_guests / stats.total_guests) * 100) : 0}% checked in · {stats.checked_in_count}/{stats.total_bookings} bookings
                </p>
                {stats.no_show_count > 0 && (
                  <p data-testid="no-show-count" className="text-xs uppercase tracking-[0.15em] text-[#C44536] mt-1.5 inline-flex items-center gap-1.5 justify-end">
                    <XCircle size={13} /> {stats.no_show_count} no-show{stats.no_show_count !== 1 ? "s" : ""} · {stats.no_show_guests} guest{stats.no_show_guests !== 1 ? "s" : ""}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-5 h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all duration-700" style={{ width: `${stats.total_guests > 0 ? (stats.checked_in_guests / stats.total_guests) * 100 : 0}%` }} />
            </div>
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            <Stat icon={TrendingUp} label="Total Revenue" value={formatISK(stats.total_revenue)} testid="stat-revenue" />
            <Stat icon={Users} label="Total Guests" value={stats.total_guests} testid="stat-guests" />
            <Stat icon={Compass} label="Tour Bookings" value={stats.tour_count} testid="stat-tours" />
            <Stat icon={Utensils} label="Dinner Reservations" value={stats.dinner_count} testid="stat-dinners" />
          </div>
        )}

        {stats && (
          <div data-testid="income-card" className="bg-gradient-to-br from-emerald-950/40 to-[#0C0C0E] border border-emerald-500/25 p-6 md:p-8 mb-12">
            <div className="flex items-center gap-4 mb-6 flex-wrap">
              <div className="w-12 h-12 rounded-full border border-emerald-500/40 flex items-center justify-center">
                <TrendingUp className="text-emerald-400" size={20} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-emerald-400/80">Total Income</p>
                <p className="text-xs text-gray-500 mt-1">Paid bookings only · base price before 4% card fee</p>
              </div>
              <div className="ml-auto text-right">
                <p data-testid="stat-total-income" className="font-display font-extrabold text-4xl leading-none text-emerald-400">{formatISK(stats.total_income ?? 0)}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-black/30 border border-white/10 p-5">
                <div className="flex items-center gap-2 mb-3"><Compass size={15} className="text-sky-400" /><p className="text-xs uppercase tracking-[0.15em] text-gray-400">Activities</p></div>
                <p data-testid="stat-activity-income" className="font-display font-bold text-2xl">{formatISK(stats.activity_income ?? 0)}</p>
                <p className="text-xs text-gray-600 mt-1">Tours &amp; buggy</p>
              </div>
              <div className="bg-black/30 border border-white/10 p-5">
                <div className="flex items-center gap-2 mb-3"><Utensils size={15} className="text-amber-400" /><p className="text-xs uppercase tracking-[0.15em] text-gray-400">Dining</p></div>
                <p data-testid="stat-dinner-income" className="font-display font-bold text-2xl">{formatISK(stats.dinner_income ?? 0)}</p>
                <p className="text-xs text-gray-600 mt-1">Fine dining experience</p>
              </div>
            </div>
          </div>
        )}

        {stats && (
          <div data-testid="commission-card" className="bg-gradient-to-br from-amber-950/40 to-[#0C0C0E] border border-amber-500/25 p-6 md:p-8 mb-12">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full border border-amber-500/40 flex items-center justify-center">
                  <Percent className="text-amber-400" size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-amber-400/80">Activity Commission ({stats.commission_percent ?? COMMISSION_PERCENT}%)</p>
                  <p className="text-xs text-gray-500 mt-1">30% of paid tour &amp; buggy sales · before card fee</p>
                </div>
              </div>
              <div className="text-right">
                <p data-testid="stat-commission" className="font-display font-extrabold text-4xl leading-none text-amber-400">{formatISK(stats.activity_commission ?? 0)}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mt-2">on {formatISK(stats.activity_base_revenue ?? 0)} activity sales</p>
              </div>
            </div>
          </div>
        )}

        {analytics && (
          <section className="mb-12" data-testid="analytics-section">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5">Payments & Traffic</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Stat icon={CreditCard} label="Finished Payments" value={analytics.paid_count} testid="stat-paid" />
              <Stat icon={TrendingUp} label="Paid Revenue" value={formatISK(analytics.paid_revenue)} testid="stat-paid-revenue" />
              <Stat icon={UserCheck} label="Checked In" value={`${analytics.checked_in} / ${analytics.total_bookings}`} testid="stat-checkedin" />
              <Stat icon={Eye} label="Site Visits" value={analytics.total_views} testid="stat-views" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-[#0C0C0E] border border-white/10 p-6">
                <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-4">Visits · last 7 days</p>
                <div className="space-y-2">
                  {analytics.views_last_7_days.map((d) => {
                    const max = Math.max(1, ...analytics.views_last_7_days.map((x) => x.views));
                    return (
                      <div key={d.date} className="flex items-center gap-3">
                        <span className="text-[11px] text-gray-500 w-14">{d.date.slice(5)}</span>
                        <div className="flex-1 bg-white/5 h-3"><div className="bg-white h-3 transition-all" style={{ width: `${(d.views / max) * 100}%` }} /></div>
                        <span className="text-xs text-gray-300 w-8 text-right">{d.views}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="bg-[#0C0C0E] border border-white/10 p-6">
                <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-4">Top pages</p>
                <div className="space-y-2">
                  {analytics.top_paths.length === 0 && <p className="text-gray-600 text-xs uppercase tracking-[0.15em]">No visits yet</p>}
                  {analytics.top_paths.map((p) => (
                    <div key={p.path} className="flex justify-between text-sm"><span className="text-gray-300">{p.path}</span><span className="text-gray-500">{p.views}</span></div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Dinner stock (festival-wide portions) */}
        {dinnerStock && (
          <section className="mb-8" data-testid="dinner-stock">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">Dinner Stock <span className="text-gray-600 normal-case tracking-normal">— portions left across the whole festival (booked count against this)</span></p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[["Fire", "#D2552E"], ["Ocean", "#3E7CA6"], ["Earth", "#5E8B4C"], ["pairing", "#c9a24b"]].map(([k, color]) => {
                const s = dinnerStock[k]; if (!s) return null;
                const out = s.remaining <= 0; const low = s.remaining > 0 && s.remaining <= 10;
                return (
                  <div key={k} data-testid={`stock-${k}`} className={`border p-4 ${out ? "border-[#C44536]/60 bg-[#C44536]/10" : "border-white/10 bg-[#0C0C0E]"}`}>
                    <p className="text-[11px] uppercase tracking-[0.15em] mb-1" style={{ color }}>{k === "pairing" ? "Beverage Pairing" : k}</p>
                    <p className="text-2xl font-bold text-white" data-testid={`stock-remaining-${k}`}>{s.remaining}<span className="text-sm text-gray-500 font-normal"> / {s.total}</span></p>
                    <p className={`text-[11px] mt-1 ${out ? "text-[#e07a6c] font-bold uppercase tracking-[0.1em]" : low ? "text-amber-400" : "text-gray-500"}`}>{out ? "Sold out" : `${s.used} booked`}</p>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Dinner capacity grid */}
        {stats && (() => {
          const seatingKeys = Array.from(new Set(Object.values(stats.dinner_capacity).flatMap((s) => Object.keys(s)))).sort();
          const labelMap = { "15:00": "Dinner", "18:00": "Dinner", "21:00": "Dinner" };
          const threshold = settings.low_seats_threshold ?? 10;
          const lowList = [];
          Object.entries(stats.dinner_capacity).forEach(([d, s]) => {
            seatingKeys.forEach((k) => {
              const c = s[k];
              if (c && !c.blocked && !c.closed && c.remaining > 0 && c.remaining <= threshold) {
                lowList.push({ date: d, seating: k, remaining: c.remaining });
              }
            });
          });
          return (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Dining Capacity <span className="text-gray-600 normal-case tracking-normal">— each sitting has its own cap · click a cap to edit</span></p>
              <button data-testid="toggle-cap-history" onClick={() => setShowHistory((v) => !v)} className="text-xs uppercase tracking-[0.15em] text-gray-400 hover:text-white inline-flex items-center gap-2 transition-colors"><History size={14} /> {showHistory ? "Hide" : "Show"} change log</button>
            </div>
            {lowList.length > 0 && (
              <div data-testid="low-seats-alert" className="mb-4 border border-amber-500/40 bg-amber-500/10 px-5 py-4 flex items-start gap-3">
                <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-200">
                  <span className="font-semibold">Low seats</span> <span className="text-amber-300/70">(≤ {threshold} left):</span>{" "}
                  {lowList.map((l, i) => (
                    <span key={`${l.date}-${l.seating}`}>{i > 0 ? ", " : ""}{formatDate(l.date)} {l.seating} <span className="text-amber-300/80">({l.remaining} left)</span></span>
                  ))}
                </div>
              </div>
            )}
            <div className="overflow-x-auto border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-[#0C0C0E]">
                  <tr className="text-left text-xs uppercase tracking-[0.15em] text-gray-500">
                    <th className="py-4 px-5">Date</th>
                    {seatingKeys.map((k) => (
                      <th key={k} className="py-4 px-5">{labelMap[k] || k} ({k}) booked / left · cap</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(stats.dinner_capacity).map(([d, s]) => (
                    <tr key={d} className="border-t border-white/10" data-testid={`capacity-row-${d}`}>
                      <td className="py-4 px-5 text-gray-300">{formatDate(d)}</td>
                      {seatingKeys.map((k) => {
                        const cell = s[k];
                        const isEditing = editingCap && editingCap.date === d && editingCap.seating === k;
                        if (!cell) {
                          return <td key={k} className="py-4 px-5 text-gray-700" data-testid={`capacity-cell-${d}-${k}`}>—</td>;
                        }
                        if (cell.blocked) {
                          return <td key={k} className="py-4 px-5 text-gray-600 italic" data-testid={`capacity-cell-${d}-${k}`}>Not available</td>;
                        }
                        const isLow = !cell.closed && cell.remaining > 0 && cell.remaining <= threshold;
                        return (
                          <td key={k} className="py-4 px-5" data-testid={`capacity-cell-${d}-${k}`}>
                            <span>{cell.booked} / <span className={isLow ? "text-amber-400 font-semibold" : "text-gray-500"}>{cell.remaining}</span></span>
                            {isLow && <span data-testid={`low-badge-${d}-${k}`} className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-amber-400 border border-amber-500/40 px-1.5 py-0.5"><AlertTriangle size={10} /> Low</span>}
                            {isEditing ? (
                              <span className="inline-flex items-center gap-1 ml-3">
                                <input autoFocus type="number" min="0" value={capValue} onChange={(e) => setCapValue(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === "Enter") saveCapacity(); if (e.key === "Escape") setEditingCap(null); }}
                                  data-testid={`capacity-input-${d}-${k}`}
                                  className="w-16 bg-black border border-white/25 px-2 py-1 text-xs text-white focus:outline-none focus:border-white/60" />
                                <button disabled={savingCap} onClick={saveCapacity} data-testid={`capacity-save-${d}-${k}`} className="text-emerald-400 hover:text-emerald-300"><CheckCircle2 size={16} /></button>
                                <button onClick={() => setEditingCap(null)} className="text-gray-500 hover:text-gray-300"><X size={16} /></button>
                              </span>
                            ) : (
                              <button onClick={() => { setEditingCap({ date: d, seating: k }); setCapValue(String(cell.capacity)); }}
                                data-testid={`capacity-edit-${d}-${k}`}
                                className="inline-flex items-center gap-1 ml-3 text-xs uppercase tracking-[0.1em] text-gray-500 hover:text-white transition-colors">
                                cap {cell.capacity} <Pencil size={12} />
                              </button>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {showHistory && (
              <div data-testid="cap-history" className="mt-4 border border-white/10 bg-[#0C0C0E]">
                <p className="text-xs uppercase tracking-[0.15em] text-gray-500 px-5 py-3 border-b border-white/10">Capacity change log</p>
                {capHistory.length === 0 ? (
                  <p className="px-5 py-4 text-sm text-gray-500">No capacity changes recorded yet.</p>
                ) : (
                  <ul className="divide-y divide-white/5">
                    {capHistory.map((h) => (
                      <li key={h.id} className="px-5 py-3 text-sm flex flex-wrap items-center gap-x-2 gap-y-1" data-testid={`cap-history-row-${h.id}`}>
                        <span className="text-gray-300">{formatDate(h.date)} · {h.seating}</span>
                        <span className="text-gray-500">cap</span>
                        <span className="text-gray-400">{h.old_capacity}</span>
                        <span className="text-gray-600">→</span>
                        <span className="text-white font-semibold">{h.new_capacity}</span>
                        <span className="text-gray-600 ml-auto text-xs">{h.changed_by} · {fmtDateTime(h.changed_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
          );
        })()}

        {/* Activity Report — transfer / car planner (paid tours, explicit car assignment) */}
        {(() => {
          const cancelledNames = new Set(data.cancelled_tour_names || []);
          const paidTours = (data.tour_bookings || []).filter((b) => b.payment_status === "paid" && !cancelledNames.has(b.tour_name));
          if (paidTours.length === 0) return null;
          const money = (n) => formatISK(n || 0);
          const SMALL_GROUP = 4;
          const px = (b) => (b.adults || 0) + (b.children || 0);
          const todayISO = new Date().toLocaleDateString("en-CA");
          const sortB = (a, b) => (a.tour_name || "").localeCompare(b.tour_name || "") || (a.slot || "").localeCompare(b.slot || "") || (a.customer?.full_name || "").localeCompare(b.customer?.full_name || "");
          const dates = Array.from(new Set(paidTours.map((b) => b.date))).sort();
          const costOf = (b) => {
            const c = activityCosts[b.tour_id] || { transfer: 0, operator: 0 };
            const pax = px(b);
            const transfer = (c.transfer || 0) * pax;
            const operator = (c.operator || 0) * pax;
            return { transfer, operator, total: transfer + operator };
          };
          const reschedBadge = (b) => {
            let label = null, cls = "";
            if (b.reschedule_status === "rescheduled") { label = "Moved"; cls = "text-emerald-400 border-emerald-500/40"; }
            else if (b.private_transfer_requested) { label = "Private transfer req."; cls = "text-amber-300 border-amber-500/40"; }
            else if (b.reschedule_status === "quote_sent") { label = "Quote sent"; cls = "text-amber-300 border-amber-500/40"; }
            else if (b.reschedule_status === "invited") { label = "Reschedule sent"; cls = "text-[#e3c67d] border-[#c9a24b]/40"; }
            if (!label) return null;
            return <span data-testid={`plan-resched-status-${b.id}`} className={`ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] border px-1.5 py-0.5 align-middle ${cls}`}><CalendarClock size={9} /> {label}</span>;
          };
          const bookingLine = (b, inCar, dayCars) => {
            const g = px(b);
            const small = g > 0 && g < SMALL_GROUP;
            return (
              <div key={b.id} data-testid={`plan-booking-${b.id}`} draggable
                onDragStart={(e) => { setDragBooking({ id: b.id, date: b.date }); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", b.id); }}
                onDragEnd={() => setDragBooking(null)}
                className={`flex items-center gap-3 px-4 py-3 border-t border-white/5 cursor-grab active:cursor-grabbing ${small ? "bg-amber-500/[0.05]" : ""}`}>
                <GripVertical size={14} className="text-gray-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{b.tour_name || "Unknown"} {small && <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-amber-400 border border-amber-500/40 px-1.5 py-0.5 align-middle"><AlertTriangle size={9} /> Small</span>}{reschedBadge(b)}</p>
                  <p className="text-xs text-gray-500 truncate">{b.slot ? `${b.slot} · ` : ""}{b.customer?.full_name || "—"} · {g} pax</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] uppercase tracking-[0.12em] text-gray-500">Transfer ISK</span>
                  <input type="number" min="0" data-testid={`plan-transfer-${b.id}`} value={transferPrices[b.id] || ""} placeholder="0"
                    onChange={(e) => setTransferPrice(b.id, e.target.value === "" ? 0 : Number(e.target.value))}
                    onBlur={persistPlan}
                    className="w-24 bg-black border border-white/20 px-2 py-1.5 text-sm text-white text-right focus:outline-none focus:border-white/50" />
                </div>
                {(() => { const c = costOf(b); return c.total > 0 ? (
                  <span data-testid={`plan-cost-${b.id}`} title={`Transfer ${c.transfer.toLocaleString("en-US")} + Operator ${c.operator.toLocaleString("en-US")} ISK`} className="shrink-0 text-[10px] uppercase tracking-[0.1em] text-amber-300/90 border border-amber-500/30 px-2 py-1">Cost {c.total.toLocaleString("en-US")}</span>
                ) : null; })()}
                <div className="flex items-center gap-1.5 shrink-0">
                  <button data-testid={`plan-edit-${b.id}`} onClick={() => openEdit(b)} title="Change date/time directly (guest not notified)" className="text-gray-500 hover:text-white border border-white/10 hover:border-white/40 p-1.5 transition-colors"><Pencil size={13} /></button>
                  <button data-testid={`plan-message-${b.id}`} onClick={() => openMessage(b)} title="Send an email to this client" className="text-gray-500 hover:text-sky-300 border border-white/10 hover:border-sky-400/40 p-1.5 transition-colors"><MessageSquare size={13} /></button>
                  <button data-testid={`plan-reschedule-${b.id}`} onClick={() => openReschedule(b)} title="Ask the client to pick a new date/time" className="text-gray-500 hover:text-[#e3c67d] border border-white/10 hover:border-[#c9a24b]/40 p-1.5 transition-colors"><CalendarClock size={13} /></button>
                </div>
                {inCar ? (
                  <button data-testid={`plan-remove-${b.id}`} onClick={() => assignBooking(b.id, null)} title="Remove from car" className="shrink-0 text-[10px] uppercase tracking-[0.1em] text-gray-500 hover:text-red-400 border border-white/10 hover:border-red-500/40 px-2 py-1.5 inline-flex items-center gap-1"><X size={12} /> Remove</button>
                ) : (
                  <select data-testid={`plan-assign-${b.id}`} value="" onChange={(e) => { if (e.target.value) assignBooking(b.id, e.target.value); }}
                    className="shrink-0 bg-black border border-white/15 text-gray-300 text-[11px] uppercase tracking-[0.1em] px-2 py-1.5 focus:outline-none focus:border-white/50">
                    <option value="">Assign to…</option>
                    {dayCars.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                )}
              </div>
            );
          };
          return (
            <section data-testid="activity-report" className="mb-12">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Compass size={18} className="text-sky-400" />
                <h2 className="text-sm uppercase tracking-[0.2em] text-gray-300">Activity Report · Transfer Planner</h2>
                <span className="text-xs text-gray-600">· organised by day · drag guests into a car · cars only hold guests from their own day</span>
                <button data-testid="export-activity-csv" onClick={exportActivityCSV} className="ml-auto uppercase tracking-[0.12em] text-[11px] font-bold py-2 px-3 border border-white/15 text-gray-300 hover:border-white/40 transition-all inline-flex items-center gap-2"><Download size={13} /> CSV</button>
              </div>
              <div className="space-y-8" data-testid="transfer-plan">
                {dates.map((date) => {
                  const dayAll = paidTours.filter((b) => b.date === date);
                  const dayBookings = dayAll.filter(needsTransfer);
                  const dayOnSite = dayAll.filter(isOnSite).sort(sortB);
                  const dayCars = cars.filter((c) => c.date === date);
                  const dayUnassigned = dayBookings.filter((b) => !assignments[b.id]).sort(sortB);
                  const dropAllowed = dragBooking && dragBooking.date === date;
                  const isPast = date < todayISO;
                  const dayPax = dayAll.reduce((s, b) => s + px(b), 0);
                  const didPax = dayAll.filter((b) => b.checked_in).reduce((s, b) => s + px(b), 0);
                  const noShowPax = dayAll.filter((b) => b.no_show).reduce((s, b) => s + px(b), 0);
                  const byTour = Object.entries(dayAll.reduce((m, b) => { const k = b.tour_name || "Unknown"; m[k] = (m[k] || 0) + px(b); return m; }, {})).sort((a, b) => b[1] - a[1]);
                  const didList = dayAll.filter((b) => b.checked_in).sort(sortB);
                  const noShowList = dayAll.filter((b) => b.no_show).sort(sortB);
                  const costAgg = {};
                  dayAll.forEach((b) => {
                    const key = b.tour_name || "Unknown";
                    const c = costOf(b);
                    const rev = Number(b.amount_charged || b.total || 0);
                    const e = costAgg[key] || { pax: 0, transfer: 0, operator: 0, total: 0, revenue: 0 };
                    e.pax += px(b); e.transfer += c.transfer; e.operator += c.operator; e.total += c.total; e.revenue += rev;
                    costAgg[key] = e;
                  });
                  const costRows = Object.entries(costAgg).sort((a, b) => b[1].total - a[1].total);
                  const dayCostTotal = costRows.reduce((s, [, v]) => s + v.total, 0);
                  const dayRevTotal = costRows.reduce((s, [, v]) => s + v.revenue, 0);
                  return (
                    <div key={date} data-testid={`plan-day-${date}`} className="border border-sky-500/20">
                      <div className="flex items-center gap-3 px-4 py-3 bg-[#0A1520] border-b border-sky-500/20">
                        <CalendarClock size={16} className="text-sky-400" />
                        <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-sky-200">{formatDate(date)}</h3>
                        <span className="text-xs text-gray-500">{dayAll.length} booking(s)</span>
                        <button data-testid={`add-car-btn-${date}`} onClick={() => addCar(date)} className="ml-auto uppercase tracking-[0.12em] text-[11px] font-bold py-2 px-3 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-all inline-flex items-center gap-2"><Plus size={13} /> Start a Car</button>
                      </div>
                      {!isPast && byTour.length > 0 && (
                        <div data-testid={`day-forecast-${date}`} className="flex flex-wrap items-center gap-2 px-4 py-3 bg-sky-500/[0.06] border-b border-sky-500/15">
                          <span className="text-[10px] uppercase tracking-[0.14em] text-sky-300 font-bold mr-1">Booked</span>
                          {byTour.map(([name, n]) => (
                            <span key={name} data-testid={`forecast-tour-${date}`} className="inline-flex items-center gap-1 text-[11px] text-sky-100/90 border border-sky-500/25 px-2 py-1">{name} <span className="text-sky-300 font-bold">×{n}</span></span>
                          ))}
                          <span className="text-[11px] text-gray-500 ml-auto">{dayPax} guest{dayPax !== 1 ? "s" : ""} total</span>
                        </div>
                      )}
                      <div className="p-4 space-y-4">
                        {dayCars.map((car) => {
                          const members = dayBookings.filter((b) => assignments[b.id] === car.id).sort(sortB);
                          const paxUsed = members.reduce((s, b) => s + px(b), 0);
                          const cap = car.capacity || 0;
                          const over = cap > 0 && paxUsed > cap;
                          const transfers = members.reduce((s, b) => s + (Number(transferPrices[b.id]) || 0), 0);
                          const cost = car.cost || 0;
                          const profit = transfers - cost;
                          const actCounts = Object.entries(members.reduce((m, b) => { const k = b.tour_name || "Unknown"; m[k] = (m[k] || 0) + px(b); return m; }, {}));
                          return (
                            <div key={car.id} onDragOver={(e) => { if (dropAllowed) e.preventDefault(); }} onDrop={(e) => { e.preventDefault(); if (dragBooking && dragBooking.date === date) assignBooking(dragBooking.id, car.id); }}
                              className={`border transition-colors ${over ? "border-red-500/50" : dropAllowed ? "border-amber-400 bg-amber-500/[0.04]" : "border-amber-500/25"}`} data-testid={`plan-car-${car.id}`}>
                              <div className="flex items-center gap-3 px-4 py-3 bg-[#141109] flex-wrap">
                                <Car size={16} className="text-amber-400 shrink-0" />
                                <input data-testid={`car-label-${car.id}`} value={car.label || ""} onChange={(e) => updateCar(car.id, { label: e.target.value })} onBlur={persistPlan}
                                  className="bg-transparent border-b border-amber-500/30 text-amber-200 font-semibold uppercase tracking-[0.1em] text-sm px-1 py-1 focus:outline-none focus:border-amber-400 w-28" />
                                <span className={`text-xs font-bold px-2 py-1 border ${over ? "border-red-500/50 text-red-400" : "border-white/15 text-gray-300"}`} data-testid={`car-pax-${car.id}`}>{paxUsed} / {cap} pax{over ? " · OVER" : ""}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] uppercase tracking-[0.12em] text-gray-500">Seats</span>
                                  <input type="number" min="0" data-testid={`car-capacity-${car.id}`} value={car.capacity || ""} placeholder="0"
                                    onChange={(e) => updateCar(car.id, { capacity: e.target.value === "" ? 0 : Number(e.target.value) })} onBlur={persistPlan}
                                    className="w-16 bg-black border border-white/20 px-2 py-1.5 text-sm text-white text-right focus:outline-none focus:border-white/50" />
                                </div>
                                <div className="flex items-center gap-2 ml-auto shrink-0">
                                  <span className="text-[10px] uppercase tracking-[0.12em] text-gray-500">Car cost ISK</span>
                                  <input type="number" min="0" data-testid={`car-cost-${car.id}`} value={car.cost || ""} placeholder="0"
                                    onChange={(e) => updateCar(car.id, { cost: e.target.value === "" ? 0 : Number(e.target.value) })} onBlur={persistPlan}
                                    className="w-28 bg-black border border-white/20 px-2 py-1.5 text-sm text-white text-right focus:outline-none focus:border-white/50" />
                                  <button data-testid={`car-reschedule-${car.id}`} onClick={() => openCarReschedule(car, members)} disabled={members.length === 0} title="Email all guests in this car to reschedule" className="text-gray-500 hover:text-[#e3c67d] disabled:opacity-30 disabled:hover:text-gray-500"><CalendarClock size={15} /></button>
                                  <button data-testid={`car-remove-${car.id}`} onClick={() => removeCar(car.id)} title="Remove car (frees its guests)" className="text-gray-500 hover:text-red-400"><Trash2 size={15} /></button>
                                </div>
                              </div>
                              {actCounts.length > 0 && (
                                <div data-testid={`car-activity-subtotals-${car.id}`} className="flex flex-wrap gap-2 px-4 py-2.5 border-t border-white/5 bg-black/20">
                                  {actCounts.map(([name, n]) => (
                                    <span key={name} className="inline-flex items-center gap-1 text-[11px] text-amber-200/90 border border-amber-500/25 px-2 py-1">{name} <span className="text-amber-400 font-bold">×{n}</span></span>
                                  ))}
                                </div>
                              )}
                              {members.length === 0 ? (
                                <p className="px-4 py-4 text-xs text-gray-600 border-t border-white/5">{dropAllowed ? "Drop the guest here" : "Drag a guest here, or use the Assign dropdown below."}</p>
                              ) : members.map((b) => bookingLine(b, true, dayCars))}
                              <div className="flex items-center justify-end gap-6 px-4 py-3 border-t border-white/10 bg-black/40 text-sm" data-testid={`car-summary-${car.id}`}>
                                <span className="text-gray-400">Transfers <span className="text-white font-semibold" data-testid={`car-transfers-${car.id}`}>{money(transfers)}</span></span>
                                <span className="text-gray-400">− Car cost <span className="text-white font-semibold">{money(cost)}</span></span>
                                <span className={`font-bold ${profit >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid={`car-profit-${car.id}`}>Profit {money(profit)}</span>
                              </div>
                            </div>
                          );
                        })}
                        <div onDragOver={(e) => { if (dropAllowed) e.preventDefault(); }} onDrop={(e) => { e.preventDefault(); if (dragBooking && dragBooking.date === date) assignBooking(dragBooking.id, null); }}
                          className={`border border-dashed transition-colors ${dropAllowed ? "border-sky-400 bg-sky-500/[0.04]" : "border-white/10"}`} data-testid={`plan-unassigned-${date}`}>
                          <p className="px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] text-gray-500 bg-[#0C0C0E]">Not in a car yet ({dayUnassigned.length})</p>
                          {dayUnassigned.length === 0 ? (
                            <p className="px-4 py-4 text-xs text-gray-600">Every guest this day is assigned to a car.</p>
                          ) : dayUnassigned.map((b) => bookingLine(b, false, dayCars))}
                        </div>
                        {dayOnSite.length > 0 && (
                          <div data-testid={`plan-onsite-${date}`} className="border border-amber-500/30 bg-amber-500/[0.04]">
                            <p className="px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] text-amber-300 bg-amber-500/[0.08] inline-flex items-center gap-2 w-full"><Lock size={12} /> On-site · no transfer needed ({dayOnSite.length})</p>
                            {dayOnSite.map((b) => {
                              const g = px(b);
                              return (
                                <div key={b.id} data-testid={`plan-onsite-booking-${b.id}`} className="flex items-center gap-3 px-4 py-3 border-t border-amber-500/10">
                                  <MapPin size={14} className="text-amber-400/70 shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-amber-100/90 text-sm truncate">{b.tour_name || "Unknown"}</p>
                                    <p className="text-xs text-amber-200/40 truncate">{b.slot ? `${b.slot} · ` : ""}{b.customer?.full_name || "—"} · {g} pax</p>
                                  </div>
                                  <span className="shrink-0 text-[10px] uppercase tracking-[0.12em] text-amber-300/80 border border-amber-500/30 px-2 py-1 inline-flex items-center gap-1"><Lock size={10} /> At Viking Village</span>
                                  {b.no_show ? (
                                    <button data-testid={`onsite-undo-noshow-${b.id}`} onClick={() => toggleNoShow(b)} className="shrink-0 text-[10px] uppercase tracking-[0.12em] text-[#C44536] border border-[#C44536]/40 px-2.5 py-1.5 hover:bg-[#C44536]/10 transition-colors inline-flex items-center gap-1"><XCircle size={12} /> No-show · undo</button>
                                  ) : (
                                    <button data-testid={`onsite-checkin-${b.id}`} onClick={() => toggleCheckin(b)} className={`shrink-0 text-[10px] uppercase tracking-[0.12em] px-2.5 py-1.5 border transition-colors inline-flex items-center gap-1 ${b.checked_in ? "text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10" : "text-gray-300 border-white/20 hover:border-emerald-400/50 hover:text-emerald-300"}`}>
                                      <CheckCircle2 size={12} /> {b.checked_in ? "Used ✓" : "Mark used"}
                                    </button>
                                  )}
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    {!b.no_show && (
                                      <button data-testid={`onsite-noshow-${b.id}`} onClick={() => toggleNoShow(b)} title="Mark as no-show" className="text-amber-200/50 hover:text-[#C44536] border border-amber-500/20 hover:border-[#C44536]/40 p-1.5 transition-colors"><MinusCircle size={13} /></button>
                                    )}
                                    <button data-testid={`onsite-message-${b.id}`} onClick={() => openMessage(b)} title="Send an email to this client" className="text-amber-200/50 hover:text-sky-300 border border-amber-500/20 hover:border-sky-400/40 p-1.5 transition-colors"><MessageSquare size={13} /></button>
                                    <button data-testid={`onsite-edit-${b.id}`} onClick={() => openEdit(b)} title="Change date/time directly (guest not notified)" className="text-amber-200/50 hover:text-white border border-amber-500/20 hover:border-white/40 p-1.5 transition-colors"><Pencil size={13} /></button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {(didList.length > 0 || noShowList.length > 0 || isPast) && dayAll.length > 0 && (
                          <div data-testid={`day-recap-${date}`} className="px-4 py-3 mt-1 bg-white/[0.03] border border-white/10 text-sm space-y-2.5">
                            <div className="flex flex-wrap items-center gap-3">
                              <span className="text-[10px] uppercase tracking-[0.14em] text-gray-400 font-bold">{isPast ? "End of day" : "Attendance"}</span>
                              <span className="text-emerald-400 font-semibold" data-testid={`recap-did-${date}`}>{didPax} {isPast ? "did the tour" : "checked in"}</span>
                              <span className="text-gray-600">·</span>
                              <span className="text-[#C44536] font-semibold" data-testid={`recap-noshow-${date}`}>{noShowPax} no-show{noShowPax !== 1 ? "s" : ""}</span>
                              <span className="text-gray-500 ml-auto">of {dayPax} booked</span>
                            </div>
                            {didList.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 items-center">
                                <span className="text-[10px] uppercase tracking-[0.12em] text-emerald-400/70 mr-1">Checked in</span>
                                {didList.map((b) => (
                                  <span key={b.id} data-testid={`recap-did-name-${b.id}`} title={`${b.tour_name || ""}${b.slot ? " · " + b.slot : ""}`} className="inline-flex items-center gap-1 text-[11px] text-emerald-200/90 border border-emerald-500/25 px-2 py-0.5">
                                    <CheckCircle2 size={10} /> {b.customer?.full_name || "—"} <span className="text-emerald-400/60">·{px(b)}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                            {noShowList.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 items-center">
                                <span className="text-[10px] uppercase tracking-[0.12em] text-[#C44536]/80 mr-1">No-shows</span>
                                {noShowList.map((b) => (
                                  <span key={b.id} data-testid={`recap-noshow-name-${b.id}`} title={`${b.tour_name || ""}${b.slot ? " · " + b.slot : ""}`} className="inline-flex items-center gap-1 text-[11px] text-[#e07a6c] border border-[#C44536]/30 px-2 py-0.5">
                                    <XCircle size={10} /> {b.customer?.full_name || "—"} <span className="text-[#C44536]/60">·{px(b)}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {dayCostTotal > 0 && (
                          <div data-testid={`day-costs-${date}`} className="mt-1 border border-amber-500/20 bg-amber-500/[0.03]">
                            <p className="px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] text-amber-300 bg-amber-500/[0.06] inline-flex items-center gap-2 w-full"><BadgeDollarSign size={12} /> Costs &amp; profit per activity</p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm min-w-[560px]">
                                <thead>
                                  <tr className="text-[10px] uppercase tracking-[0.1em] text-gray-500">
                                    <th className="text-left font-medium py-2 px-4">Activity</th>
                                    <th className="text-right font-medium py-2 px-2">Pax</th>
                                    <th className="text-right font-medium py-2 px-2">Transfer</th>
                                    <th className="text-right font-medium py-2 px-2">Operator</th>
                                    <th className="text-right font-medium py-2 px-2">Total cost</th>
                                    <th className="text-right font-medium py-2 px-2">Revenue</th>
                                    <th className="text-right font-medium py-2 px-4">Profit</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {costRows.map(([name, v]) => (
                                    <tr key={name} data-testid={`day-cost-row-${date}`} className="border-t border-white/5">
                                      <td className="py-2 px-4 text-gray-200">{name}</td>
                                      <td className="py-2 px-2 text-right text-gray-400">{v.pax}</td>
                                      <td className="py-2 px-2 text-right text-gray-400">{v.transfer.toLocaleString("en-US")}</td>
                                      <td className="py-2 px-2 text-right text-gray-400">{v.operator.toLocaleString("en-US")}</td>
                                      <td className="py-2 px-2 text-right text-amber-300">{v.total.toLocaleString("en-US")}</td>
                                      <td className="py-2 px-2 text-right text-gray-300">{v.revenue.toLocaleString("en-US")}</td>
                                      <td className={`py-2 px-4 text-right font-semibold ${v.revenue - v.total >= 0 ? "text-emerald-400" : "text-red-400"}`}>{(v.revenue - v.total).toLocaleString("en-US")}</td>
                                    </tr>
                                  ))}
                                  <tr className="border-t border-amber-500/20 font-bold">
                                    <td className="py-2.5 px-4 text-white uppercase text-[11px] tracking-[0.1em]">Day total</td>
                                    <td className="py-2.5 px-2 text-right text-gray-300">{dayPax}</td>
                                    <td className="py-2.5 px-2"></td>
                                    <td className="py-2.5 px-2"></td>
                                    <td data-testid={`day-cost-total-${date}`} className="py-2.5 px-2 text-right text-amber-300">{dayCostTotal.toLocaleString("en-US")}</td>
                                    <td className="py-2.5 px-2 text-right text-gray-200">{dayRevTotal.toLocaleString("en-US")}</td>
                                    <td className={`py-2.5 px-4 text-right ${dayRevTotal - dayCostTotal >= 0 ? "text-emerald-400" : "text-red-400"}`}>{(dayRevTotal - dayCostTotal).toLocaleString("en-US")}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })()}

        {/* Kitchen Report — paid dinners grouped by date + seating */}
        {(() => {
          const paidDinners = (data.dinner_reservations || []).filter((d) => d.payment_status === "paid");
          if (paidDinners.length === 0) return null;
          const groups = {};
          paidDinners.forEach((d) => {
            const key = `${d.date}__${d.seating}`;
            if (!groups[key]) groups[key] = { date: d.date, seating: d.seating, seating_label: d.seating_label || "", guests: 0, Fire: 0, Ocean: 0, Earth: 0, pairings: 0, allergies: [] };
            const g = groups[key];
            g.guests += d.party_size || 0;
            g.Fire += d.journeys?.Fire || 0;
            g.Ocean += d.journeys?.Ocean || 0;
            g.Earth += d.journeys?.Earth || 0;
            g.pairings += d.beverage_count || 0;
            const al = (d.customer?.allergies || "").trim();
            if (al) g.allergies.push({ name: d.customer?.full_name || "Guest", party: d.party_size || 1, text: al });
          });
          const rows = Object.values(groups).sort((a, b) => a.date.localeCompare(b.date) || a.seating.localeCompare(b.seating));
          const allergyRows = rows.filter((r) => r.allergies.length > 0);
          const totals = rows.reduce((t, r) => ({ guests: t.guests + r.guests, Fire: t.Fire + r.Fire, Ocean: t.Ocean + r.Ocean, Earth: t.Earth + r.Earth, pairings: t.pairings + r.pairings }), { guests: 0, Fire: 0, Ocean: 0, Earth: 0, pairings: 0 });
          return (
            <section data-testid="kitchen-report" className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <ChefHat size={18} className="text-amber-400" />
                <h2 className="text-sm uppercase tracking-[0.2em] text-gray-300">Kitchen Report</h2>
                <span className="text-xs text-gray-600">· prep by night &amp; sitting (paid only)</span>
                <button data-testid="export-kitchen-csv" onClick={exportKitchenCSV} className="ml-auto uppercase tracking-[0.12em] text-[11px] font-bold py-2 px-3 border border-white/15 text-gray-300 hover:border-white/40 transition-all inline-flex items-center gap-2"><Download size={13} /> CSV</button>
                <button data-testid="print-prep-sheet" onClick={printPrepSheet} className="uppercase tracking-[0.12em] text-[11px] font-bold py-2 px-3 border border-amber-400/40 text-amber-300 hover:border-amber-400 hover:bg-amber-400/10 transition-all inline-flex items-center gap-2"><Printer size={13} /> Prep Sheet</button>
                <button data-testid="save-prep-pdf" onClick={savePrepSheetPDF} className="uppercase tracking-[0.12em] text-[11px] font-bold py-2 px-3 border border-amber-400/40 text-amber-300 hover:border-amber-400 hover:bg-amber-400/10 transition-all inline-flex items-center gap-2"><FileText size={13} /> Save PDF</button>
              </div>
              <div className="overflow-x-auto border border-white/10">
                <table className="w-full text-sm" data-testid="kitchen-report-table">
                  <thead className="bg-[#0C0C0E]">
                    <tr className="text-left text-xs uppercase tracking-[0.15em] text-gray-500">
                      <th className="py-4 px-5">Date</th>
                      <th className="py-4 px-5">Sitting</th>
                      <th className="py-4 px-5 text-right">Guests</th>
                      <th className="py-4 px-5 text-right" style={{ color: "#D2552E" }}>Fire</th>
                      <th className="py-4 px-5 text-right" style={{ color: "#3E7CA6" }}>Ocean</th>
                      <th className="py-4 px-5 text-right" style={{ color: "#5E8B4C" }}>Earth</th>
                      <th className="py-4 px-5 text-right text-amber-400">Pairings</th>
                      <th className="py-4 px-5 text-right text-red-400">Allergies</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={`${r.date}-${r.seating}`} className="border-t border-white/10" data-testid={`kitchen-row-${r.date}-${r.seating}`}>
                        <td className="py-4 px-5 text-gray-300">{formatDate(r.date)}</td>
                        <td className="py-4 px-5 text-gray-400">{`${r.seating_label} ${r.seating}`.trim()}</td>
                        <td className="py-4 px-5 text-right text-white font-bold">{r.guests}</td>
                        <td className="py-4 px-5 text-right" style={{ color: "#D2552E" }}>{r.Fire}</td>
                        <td className="py-4 px-5 text-right" style={{ color: "#3E7CA6" }}>{r.Ocean}</td>
                        <td className="py-4 px-5 text-right" style={{ color: "#5E8B4C" }}>{r.Earth}</td>
                        <td className="py-4 px-5 text-right text-amber-400">{r.pairings}</td>
                        <td className="py-4 px-5 text-right" data-testid={`kitchen-allergy-count-${r.date}-${r.seating}`}>
                          {r.allergies.length > 0
                            ? <span className="inline-flex items-center gap-1 text-red-400 font-bold"><AlertTriangle size={13} />{r.allergies.length}</span>
                            : <span className="text-gray-600">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-white/25 bg-[#0C0C0E]" data-testid="kitchen-totals-row">
                      <td className="py-4 px-5 text-white font-bold uppercase tracking-[0.15em] text-xs" colSpan={2}>Total — all sittings</td>
                      <td className="py-4 px-5 text-right text-white font-bold" data-testid="kitchen-total-guests">{totals.guests}</td>
                      <td className="py-4 px-5 text-right font-bold" style={{ color: "#D2552E" }} data-testid="kitchen-total-fire">{totals.Fire}</td>
                      <td className="py-4 px-5 text-right font-bold" style={{ color: "#3E7CA6" }} data-testid="kitchen-total-ocean">{totals.Ocean}</td>
                      <td className="py-4 px-5 text-right font-bold" style={{ color: "#5E8B4C" }} data-testid="kitchen-total-earth">{totals.Earth}</td>
                      <td className="py-4 px-5 text-right text-amber-400 font-bold" data-testid="kitchen-total-pairings">{totals.pairings}</td>
                      <td className="py-4 px-5 text-right text-red-400 font-bold" data-testid="kitchen-total-allergies">{rows.reduce((n, r) => n + r.allergies.length, 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {allergyRows.length > 0 && (
                <div className="mt-4 border border-red-500/30 bg-red-500/[0.06]" data-testid="kitchen-allergy-panel">
                  <div className="flex items-center gap-2 px-5 py-3 border-b border-red-500/20">
                    <AlertTriangle size={15} className="text-red-400" />
                    <h3 className="text-xs uppercase tracking-[0.2em] text-red-300 font-bold">Allergy Alerts</h3>
                    <span className="text-[11px] text-red-400/70">· check before every service</span>
                  </div>
                  <div className="divide-y divide-red-500/10">
                    {allergyRows.map((r) => (
                      <div key={`al-${r.date}-${r.seating}`} className="px-5 py-3" data-testid={`kitchen-allergy-group-${r.date}-${r.seating}`}>
                        <div className="text-[11px] uppercase tracking-[0.15em] text-red-300/80 mb-2">{formatDate(r.date)} · {`${r.seating_label} ${r.seating}`.trim()}</div>
                        <div className="space-y-2">
                          {r.allergies.map((a, i) => (
                            <div key={i} className="flex flex-wrap items-baseline gap-x-2 gap-y-1" data-testid={`kitchen-allergy-item-${r.date}-${r.seating}-${i}`}>
                              <span className="text-sm text-white font-semibold">{a.name}</span>
                              <span className="text-[11px] text-gray-500">({a.party} pax)</span>
                              <span className="text-sm text-red-200 font-medium">— {a.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          );
        })()}

        {/* Tabs + tools */}
        <div className="flex gap-3 mb-6 flex-wrap items-center">
          <button data-testid="tab-tours" onClick={() => setTab("tours")} className={`uppercase tracking-[0.15em] text-xs font-bold py-3 px-6 border transition-all ${tab === "tours" ? "bg-white text-black border-white" : "border-white/15 text-gray-400 hover:border-white/40"}`}>Tour Bookings ({visible(data.tour_bookings).length})</button>
          <button data-testid="tab-dinners" onClick={() => setTab("dinners")} className={`uppercase tracking-[0.15em] text-xs font-bold py-3 px-6 border transition-all ${tab === "dinners" ? "bg-white text-black border-white" : "border-white/15 text-gray-400 hover:border-white/40"}`}>Dinner ({visible(data.dinner_reservations).length})</button>
          <div className="flex-1" />
          {tab === "dinners" && (
            <button data-testid="add-dinner-btn" onClick={() => setAddDinner({ full_name: "", email: "", phone: "", date: cfg.dates?.[0] || "", seating: (cfg.seatings || ["18:00"])[0], journeys: { Fire: 0, Ocean: 0, Earth: 0 }, beverage_count: 0, price_mode: "standard", custom_total: "" })} className="uppercase tracking-[0.12em] text-xs font-bold py-3 px-4 border border-white/15 text-white hover:border-white/50 transition-all inline-flex items-center gap-2"><Plus size={14} /> Add Dinner Booking</button>
          )}
          {tab === "dinners" && (
            <button data-testid="send-thursday-rebook-btn" onClick={sendThursdayRebook} disabled={sendingRebook} className="uppercase tracking-[0.12em] text-xs font-bold py-3 px-4 border border-[#c9a24b]/50 text-[#e3c67d] hover:bg-[#c9a24b]/10 transition-all inline-flex items-center gap-2 disabled:opacity-60"><Mail size={14} /> {sendingRebook ? "Sending…" : "Email Thursday guests to rebook"}</button>
          )}
          {tab === "dinners" && (
            <button data-testid="send-friday-rebook-btn" onClick={sendFridayRebook} disabled={sendingRebook} className="uppercase tracking-[0.12em] text-xs font-bold py-3 px-4 border border-[#C44536]/50 text-[#e07a6c] hover:bg-[#C44536]/10 transition-all inline-flex items-center gap-2 disabled:opacity-60"><XCircle size={14} /> {sendingRebook ? "Sending…" : "Cancel Friday — email paid guests"}</button>
          )}
          {tab === "dinners" && (
            <button data-testid="send-test-rebook-btn" onClick={() => sendTestRebookEmail("thursday")} className="uppercase tracking-[0.12em] text-xs font-bold py-3 px-4 border border-white/15 text-gray-300 hover:border-white/50 transition-all inline-flex items-center gap-2"><Mail size={14} /> Test (Thu)</button>
          )}
          {tab === "dinners" && (
            <button data-testid="send-test-friday-rebook-btn" onClick={() => sendTestRebookEmail("friday")} className="uppercase tracking-[0.12em] text-xs font-bold py-3 px-4 border border-white/15 text-gray-300 hover:border-white/50 transition-all inline-flex items-center gap-2"><Mail size={14} /> Test (Fri)</button>
          )}
          <button data-testid="filter-paid-only" onClick={() => setPaidOnly((v) => !v)} className={`uppercase tracking-[0.12em] text-xs font-bold py-3 px-4 border transition-all inline-flex items-center gap-2 ${paidOnly ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "border-white/15 text-gray-400 hover:border-white/40"}`}><Filter size={14} /> {paidOnly ? "Paid sales only" : "All (incl. unpaid)"}</button>
          <button data-testid="bulk-message-btn" onClick={openBulk} className="uppercase tracking-[0.12em] text-xs font-bold py-3 px-4 border border-sky-400/50 text-sky-300 hover:bg-sky-400/10 transition-all inline-flex items-center gap-2"><MessageSquare size={14} /> Bulk message</button>
          <button data-testid="export-csv" onClick={exportCSV} className="uppercase tracking-[0.12em] text-xs font-bold py-3 px-4 border border-white/15 text-gray-300 hover:border-white/40 transition-all inline-flex items-center gap-2"><Download size={14} /> Export CSV</button>
          <button data-testid="purge-unpaid" onClick={purgeUnpaid} className="uppercase tracking-[0.12em] text-xs font-bold py-3 px-4 border border-[#C44536]/40 text-[#e07a6c] hover:bg-[#C44536]/10 transition-all inline-flex items-center gap-2"><Trash2 size={14} /> Purge unpaid</button>
        </div>

        {tab === "tours" && (() => {
          const cancelledNames = new Set(data.cancelled_tour_names || []);
          const refundable = (data.tour_bookings || []).filter((b) => b.payment_status === "paid" && cancelledNames.has(b.tour_name));
          if (refundable.length === 0) return null;
          const amount = refundable.reduce((s, b) => s + Number(b.amount_charged ?? b.total ?? 0), 0);
          return (
            <div data-testid="refund-cancelled-banner" className="mb-6 bg-[#2a1210] border border-[#C44536]/40 p-5 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} className="text-[#C44536] mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm text-white">{refundable.length} cancelled-tour booking{refundable.length !== 1 ? "s" : ""} ({[...cancelledNames].join(", ")}) still counted in your sales.</p>
                  <p className="text-xs text-gray-400 mt-1">Total {formatISK(amount)}. If you've already refunded these, mark them refunded to remove them from revenue (they stay in your records).</p>
                </div>
              </div>
              <button data-testid="refund-cancelled-btn" onClick={() => refundCancelledTours(refundable.length, amount)} className="bg-[#C44536] text-white hover:bg-[#a7382b] uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center gap-2 shrink-0">
                <BadgeDollarSign size={14} /> Mark as refunded
              </button>
            </div>
          );
        })()}

        {tab === "tours" && (() => {
          const tours = data.tour_bookings || [];
          const cAsked = tours.filter((b) => b.reschedule_status === "invited").length;
          const cResched = tours.filter((b) => b.reschedule_status === "rescheduled").length;
          const cPrivate = tours.filter((b) => b.private_transfer_requested).length;
          const chip = (key, label, n, cls) => (
            <button data-testid={`resched-filter-${key}`} onClick={() => setReschedFilter(key)} className={`uppercase tracking-[0.1em] text-[11px] font-bold py-2 px-3.5 border transition-all inline-flex items-center gap-2 ${reschedFilter === key ? "bg-white text-black border-white" : `border-white/15 hover:border-white/40 ${cls}`}`}>
              <CalendarClock size={12} /> {label}{typeof n === "number" ? ` (${n})` : ""}
            </button>
          );
          return (
            <div className="flex gap-2 mb-6 flex-wrap items-center" data-testid="reschedule-tracker">
              <span className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mr-1">Reschedule tracker</span>
              {chip("all", "All", tours.length, "text-gray-400")}
              {chip("asked", "Asked", cAsked, "text-[#e3c67d]")}
              {chip("rescheduled", "Moved", cResched, "text-emerald-300")}
              {chip("private", "Private transfer", cPrivate, "text-amber-300")}
            </div>
          );
        })()}

        {tab === "dinners" && (() => {
          const dinners = data.dinner_reservations || [];
          const cInvited = dinners.filter((b) => b.rebook_status === "invited").length;
          const cRebooked = dinners.filter((b) => b.rebook_status === "rebooked").length;
          const cRefund = dinners.filter((b) => b.rebook_status === "refund_requested").length;
          const dchip = (key, label, n, cls) => (
            <button data-testid={`dinner-filter-${key}`} onClick={() => setDinnerFilter(key)} className={`uppercase tracking-[0.1em] text-[11px] font-bold py-2 px-3.5 border transition-all inline-flex items-center gap-2 ${dinnerFilter === key ? "bg-white text-black border-white" : `border-white/15 hover:border-white/40 ${cls}`}`}>
              {key === "refund" ? <BadgeDollarSign size={12} /> : <UtensilsCrossed size={12} />} {label}{typeof n === "number" ? ` (${n})` : ""}
            </button>
          );
          return (
            <div className="flex gap-2 mb-6 flex-wrap items-center" data-testid="dinner-rebook-tracker">
              <span className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mr-1">Rebook / refund tracker</span>
              {dchip("all", "All", dinners.length, "text-gray-400")}
              {dchip("invited", "Invited", cInvited, "text-[#e3c67d]")}
              {dchip("rebooked", "Rescheduled", cRebooked, "text-emerald-300")}
              {dchip("refund", "Refund requested", cRefund, "text-[#e07a6c]")}
            </div>
          );
        })()}

        <div className="overflow-x-auto border border-white/10">
          {tab === "tours" ? (
            <table className="w-full text-sm" data-testid="tours-table">
              <thead className="bg-[#0C0C0E]">
                <tr className="text-left text-xs uppercase tracking-[0.15em] text-gray-500">
                  <th className="py-4 px-5">Customer &amp; Contact</th>
                  <th className="py-4 px-5">Tour</th>
                  <th className="py-4 px-5">Date</th>
                  <th className="py-4 px-5 text-right">Pax</th>
                  <th className="py-4 px-5">Sold at</th>
                  <th className="py-4 px-5">Payment</th>
                  <th className="py-4 px-5 text-right">Paid</th>
                  <th className="py-4 px-5 text-right">Commission (30%)</th>
                  <th className="py-4 px-5 text-right">Check-in</th>
                  <th className="py-4 px-5 text-right">Manage</th>
                </tr>
              </thead>
              <tbody>
                {visible(data.tour_bookings).filter(matchesResched).length === 0 && <tr><td colSpan={10} className="py-10 px-5 text-center text-gray-600 uppercase tracking-[0.2em] text-xs">No bookings{reschedFilter !== "all" ? " for this filter" : " yet"}</td></tr>}
                {visible(data.tour_bookings).filter(matchesResched).map((b) => (
                  <tr key={b.id} className="border-t border-white/10" data-testid={`tour-row-${b.id}`}>
                    <td className="py-4 px-5"><span className="text-white">{b.customer.full_name}</span><br /><span className="text-gray-500 text-xs">{b.customer.email}</span><br /><span className="text-gray-500 text-xs">{b.customer.phone}</span>{b.customer.allergies ? <><br /><span className="text-[#C44536] text-xs">Allergies: {b.customer.allergies}</span></> : null}{b.email_bounced ? <><br /><span data-testid={`bounce-badge-${b.id}`} className="inline-flex items-center gap-1 text-[#e07a6c] text-xs font-bold mt-1">⚠ {b.email_bounce_type === "complained" ? "Marked spam" : "Email bounced"} — call guest <button data-testid={`clear-bounce-${b.id}`} onClick={() => clearBounce(b)} className="text-gray-500 hover:text-white underline ml-1 font-normal">mark called</button></span></> : null}</td>
                    <td className="py-4 px-5 text-gray-300">{b.tour_name}</td>
                    <td className="py-4 px-5 text-gray-400">{formatDate(b.date)}{b.reschedule_status === "invited" && !b.private_transfer_requested ? <><br /><span className="text-[#c9a24b] text-[11px]">Reschedule sent</span></> : null}{b.private_transfer_requested && !b.private_transfer_quote ? <><br /><span className="text-amber-400 text-[11px]">Private transfer requested</span></> : null}{b.private_transfer_quote ? <><br /><span className="text-amber-300 text-[11px]">Quote sent: {formatISK(b.private_transfer_quote)}</span></> : null}{b.reschedule_status === "rescheduled" ? <><br /><span className="text-emerald-400 text-[11px]">Rescheduled</span></> : null}{b.last_message_sent_at ? <><br /><span data-testid={`message-sent-${b.id}`} className="inline-flex items-center gap-1 text-sky-300/80 text-[11px]"><MessageSquare size={10} /> Message sent {fmtDateTime(b.last_message_sent_at)}</span></> : null}</td>
                    <td className="py-4 px-5 text-right text-white" data-testid={`tour-pax-${b.id}`}>{(b.adults || 0) + (b.children || 0)}{b.type === "buggy" && b.buggies ? <><br /><span className="text-gray-500 text-xs font-normal">{b.buggies} buggy{b.buggies > 1 ? "s" : ""}</span></> : (b.children ? <><br /><span className="text-gray-500 text-xs font-normal">{b.adults} ad · {b.children} ch</span></> : null)}</td>
                    <td className="py-4 px-5 text-gray-400 text-xs">{fmtDateTime(b.paid_at || b.created_at)}</td>
                    <td className="py-4 px-5">{payBadge(b.payment_status)}{(() => { const ls = linkStatus(b); return ls ? <><br /><span data-testid={`link-status-${b.id}`} className={`text-[10px] uppercase tracking-[0.1em] ${ls.cls}`}>{ls.label}</span></> : null; })()}</td>
                    <td className="py-4 px-5 text-right text-white">{formatISK(b.amount_charged ?? b.total)}</td>
                    <td data-testid={`commission-${b.id}`} className={`py-4 px-5 text-right ${b.payment_status === "paid" ? "text-amber-400" : "text-gray-600"}`}>{formatISK(commissionOf(b.total))}</td>
                    <td className="py-4 px-5 text-right">
                      {b.no_show
                        ? <span data-testid={`no-show-badge-${b.id}`} className="text-xs uppercase tracking-[0.1em] text-[#C44536]">No-show</span>
                        : <Link to={`/staff/checkin/${b.id}`} data-testid={`checkin-link-${b.id}`} className={`text-xs uppercase tracking-[0.1em] ${b.checked_in ? "text-emerald-400" : "text-gray-400 hover:text-white"}`}>{b.checked_in ? "Checked in" : "Check-in ›"}</Link>}
                      <br />
                      <button data-testid={`no-show-toggle-${b.id}`} onClick={() => toggleNoShow(b)} className={`text-[10px] uppercase tracking-[0.1em] mt-1 transition-colors ${b.no_show ? "text-gray-500 hover:text-white" : "text-gray-600 hover:text-[#C44536]"}`}>{b.no_show ? "Undo no-show" : "Mark no-show"}</button>
                    </td>
                    <td className="py-4 px-5 text-right">
                      <div className="inline-flex items-center gap-3">
                        <button data-testid={`edit-booking-${b.id}`} onClick={() => openEdit(b)} className="text-gray-500 hover:text-white transition-colors" title="Edit date/time"><Pencil size={15} /></button>
                        {b.type !== "buggy" && <button data-testid={`reschedule-booking-${b.id}`} onClick={() => openReschedule(b)} className="text-gray-500 hover:text-[#e3c67d] transition-colors" title="Ask client to reschedule"><CalendarClock size={15} /></button>}
                        <button data-testid={`message-booking-${b.id}`} onClick={() => openMessage(b)} className="text-gray-500 hover:text-sky-300 transition-colors" title="Email the guest about a change"><MessageSquare size={15} /></button>
                        {b.private_transfer_requested && <button data-testid={`quote-booking-${b.id}`} onClick={() => openQuote(b)} className={`transition-colors ${b.private_transfer_quote ? "text-amber-300 hover:text-amber-200" : "text-amber-400 hover:text-amber-200"}`} title={b.private_transfer_quote ? "Update / resend private transfer quote" : "Send private transfer quote"}><Car size={15} /></button>}
                        <button data-testid={`resend-email-${b.id}`} onClick={() => resendEmail(b)} className="text-gray-500 hover:text-white transition-colors" title="Resend email with QR"><Mail size={15} /></button>
                        {b.payment_status === "paid" && <button data-testid={`refund-cancel-${b.id}`} onClick={() => refundCancelBooking(b)} className="text-gray-500 hover:text-[#C44536] transition-colors" title="Mark refunded & cancelled"><BadgeDollarSign size={15} /></button>}
                        <button data-testid={`delete-booking-${b.id}`} onClick={() => deleteBooking(b)} className="text-gray-500 hover:text-[#e07a6c] transition-colors" title="Delete booking"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm" data-testid="dinners-table">
              <thead className="bg-[#0C0C0E]">
                <tr className="text-left text-xs uppercase tracking-[0.15em] text-gray-500">
                  <th className="py-4 px-5">Customer &amp; Contact</th>
                  <th className="py-4 px-5">Date</th>
                  <th className="py-4 px-5">Seating</th>
                  <th className="py-4 px-5">Party</th>
                  <th className="py-4 px-5">Sold at</th>
                  <th className="py-4 px-5">Payment</th>
                  <th className="py-4 px-5 text-right">Paid</th>
                  <th className="py-4 px-5 text-right">Check-in</th>
                  <th className="py-4 px-5 text-right">Manage</th>
                </tr>
              </thead>
              <tbody>
                {visible(data.dinner_reservations).filter(matchesDinner).length === 0 && <tr><td colSpan={9} className="py-10 px-5 text-center text-gray-600 uppercase tracking-[0.2em] text-xs">No reservations{dinnerFilter !== "all" ? " for this filter" : " yet"}</td></tr>}
                {visible(data.dinner_reservations).filter(matchesDinner).map((b) => (
                  <tr key={b.id} className="border-t border-white/10" data-testid={`dinner-row-${b.id}`}>
                    <td className="py-4 px-5"><span className="text-white">{b.customer.full_name}</span><br /><span className="text-gray-500 text-xs">{b.customer.email}</span><br /><span className="text-gray-500 text-xs">{b.customer.phone}</span>{b.customer.allergies ? <><br /><span className="text-[#C44536] text-xs">Allergies: {b.customer.allergies}</span></> : null}{b.email_bounced ? <><br /><span data-testid={`bounce-badge-${b.id}`} className="inline-flex items-center gap-1 text-[#e07a6c] text-xs font-bold mt-1">⚠ {b.email_bounce_type === "complained" ? "Marked spam" : "Email bounced"} — call guest <button data-testid={`clear-bounce-${b.id}`} onClick={() => clearBounce(b)} className="text-gray-500 hover:text-white underline ml-1 font-normal">mark called</button></span></> : null}</td>
                    <td className="py-4 px-5 text-gray-400">{formatDate(b.date)}
                      {b.rebook_status === "invited" ? <><br /><span data-testid={`dinner-status-${b.id}`} className="text-[#e3c67d] text-[11px]">Rebook invite sent</span></> : null}
                      {b.rebook_status === "rebooked" ? <><br /><span data-testid={`dinner-status-${b.id}`} className="text-emerald-400 text-[11px]">Rescheduled</span></> : null}
                      {b.rebook_status === "refund_requested" ? <><br /><span data-testid={`dinner-status-${b.id}`} className="inline-flex items-center gap-1 text-[#e07a6c] text-[11px] font-semibold"><BadgeDollarSign size={11} /> Refund requested — action</span></> : null}
                    </td>
                    <td className="py-4 px-5 text-gray-300">{b.seating_label ? `${b.seating_label} · ${b.seating}` : b.seating}{b.last_message_sent_at ? <><br /><span data-testid={`message-sent-${b.id}`} className="inline-flex items-center gap-1 text-sky-300/80 text-[11px]"><MessageSquare size={10} /> Message sent {fmtDateTime(b.last_message_sent_at)}</span></> : null}</td>
                    <td className="py-4 px-5 text-gray-400">{b.party_size}{b.journeys ? <><br /><span className="text-gray-500 text-xs">{Object.entries(b.journeys).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(" · ")}</span></> : null}{b.beverage_count ? <><br /><span className="text-amber-400/70 text-xs">Pairing ×{b.beverage_count}</span></> : null}</td>
                    <td className="py-4 px-5 text-gray-400 text-xs">{fmtDateTime(b.paid_at || b.created_at)}</td>
                    <td className="py-4 px-5">{payBadge(b.payment_status)}{(() => { const ls = linkStatus(b); return ls ? <><br /><span data-testid={`link-status-${b.id}`} className={`text-[10px] uppercase tracking-[0.1em] ${ls.cls}`}>{ls.label}</span></> : null; })()}</td>
                    <td className="py-4 px-5 text-right text-white">{formatISK(b.amount_charged ?? b.total)}</td>
                    <td className="py-4 px-5 text-right">
                      {b.no_show
                        ? <span data-testid={`no-show-badge-${b.id}`} className="text-xs uppercase tracking-[0.1em] text-[#C44536]">No-show</span>
                        : <Link to={`/staff/checkin/${b.id}`} data-testid={`checkin-link-${b.id}`} className={`text-xs uppercase tracking-[0.1em] ${b.checked_in ? "text-emerald-400" : "text-gray-400 hover:text-white"}`}>{b.checked_in ? "Checked in" : "Check-in ›"}</Link>}
                      <br />
                      <button data-testid={`no-show-toggle-${b.id}`} onClick={() => toggleNoShow(b)} className={`text-[10px] uppercase tracking-[0.1em] mt-1 transition-colors ${b.no_show ? "text-gray-500 hover:text-white" : "text-gray-600 hover:text-[#C44536]"}`}>{b.no_show ? "Undo no-show" : "Mark no-show"}</button>
                    </td>
                    <td className="py-4 px-5 text-right">
                      <div className="inline-flex items-center gap-3">
                        <button data-testid={`edit-booking-${b.id}`} onClick={() => openEdit(b)} className="text-gray-500 hover:text-white transition-colors" title="Edit date/time"><Pencil size={15} /></button>
                        <button data-testid={`message-booking-${b.id}`} onClick={() => openMessage(b)} className="text-gray-500 hover:text-sky-300 transition-colors" title="Email the guest about a change"><MessageSquare size={15} /></button>
                        <button data-testid={`resend-email-${b.id}`} onClick={() => resendEmail(b)} className="text-gray-500 hover:text-white transition-colors" title="Resend email with QR"><Mail size={15} /></button>
                        {b.payment_status === "paid" && <button data-testid={`refund-cancel-${b.id}`} onClick={() => refundCancelBooking(b)} className="text-gray-500 hover:text-[#C44536] transition-colors" title="Mark refunded & cancelled"><BadgeDollarSign size={15} /></button>}
                        <button data-testid={`delete-booking-${b.id}`} onClick={() => deleteBooking(b)} className="text-gray-500 hover:text-[#e07a6c] transition-colors" title="Delete booking"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showDeleted && (
        <div data-testid="deleted-sales-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowDeleted(false)}>
          <div className="bg-[#0C0C0E] border border-white/15 w-full max-w-2xl max-h-[85vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#e07a6c] mb-1">Record</p>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl">Deleted confirmed sales</h3>
                <p className="text-[11px] text-gray-500 mt-1">Paid bookings that were deleted are kept here. Unpaid/abandoned carts are not recorded.</p>
              </div>
              <button data-testid="deleted-sales-close" onClick={() => setShowDeleted(false)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="flex gap-2 mb-4">
              <button data-testid="deleted-filter-all" onClick={() => { setDeletedDays(null); loadDeletedSales(null); }} className={`uppercase tracking-[0.1em] text-[11px] font-bold py-2 px-3.5 border transition-all ${deletedDays === null ? "bg-white text-black border-white" : "border-white/15 text-gray-400 hover:border-white/40"}`}>All time</button>
              <button data-testid="deleted-filter-7" onClick={() => { setDeletedDays(7); loadDeletedSales(7); }} className={`uppercase tracking-[0.1em] text-[11px] font-bold py-2 px-3.5 border transition-all ${deletedDays === 7 ? "bg-white text-black border-white" : "border-white/15 text-gray-400 hover:border-white/40"}`}>Last 7 days</button>
              <div className="ml-auto text-right">
                <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500">Total value</p>
                <p className="text-lg font-bold text-white" data-testid="deleted-total">{formatISK(deletedSales.total_amount || 0)}</p>
              </div>
            </div>
            <div className="border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-black/40">
                  <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-gray-500">
                    <th className="py-3 px-4">Guest</th>
                    <th className="py-3 px-4">Item</th>
                    <th className="py-3 px-4 text-right">Amount</th>
                    <th className="py-3 px-4">Deleted</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedSales.records.length === 0 && <tr><td colSpan={4} className="py-8 px-4 text-center text-gray-600 uppercase tracking-[0.2em] text-xs">No deleted sales{deletedDays ? " in the last 7 days" : " recorded yet"}</td></tr>}
                  {deletedSales.records.map((d) => (
                    <tr key={d.id} className="border-t border-white/10" data-testid={`deleted-sale-${d.id}`}>
                      <td className="py-3 px-4"><span className="text-white">{d.customer_name || "—"}</span><br /><span className="text-gray-500 text-xs">{d.customer_email}</span></td>
                      <td className="py-3 px-4 text-gray-300">{d.item || "—"}<br /><span className="text-gray-500 text-xs">{d.type === "dinner" ? "Dinner" : "Tour"} · {formatDate(d.date)} · {d.party_size} pax</span></td>
                      <td className="py-3 px-4 text-right text-white">{formatISK(d.amount)}</td>
                      <td className="py-3 px-4 text-gray-400 text-xs">{fmtDateTime(d.deleted_at)}<br /><span className="text-gray-600">{d.deleted_by}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {rebookResults && (
        <div data-testid="rebook-results-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setRebookResults(null)}>          <div className="bg-[#0C0C0E] border border-white/15 w-full max-w-lg max-h-[85vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#c9a24b] mb-1">Email Delivery Report</p>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl">{rebookVariant === "friday" ? "Friday" : "Thursday"} rebooking invites</h3>
              </div>
              <button data-testid="rebook-results-close" onClick={() => setRebookResults(null)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>

            <div className="flex gap-3 mb-5">
              <div className="flex-1 border border-emerald-500/25 bg-emerald-950/20 p-3 text-center"><p className="text-2xl font-bold text-emerald-400" data-testid="rebook-sent-count">{rebookResults.sent}</p><p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mt-1">Sent</p></div>
              <div className="flex-1 border border-[#C44536]/30 bg-[#C44536]/10 p-3 text-center"><p className="text-2xl font-bold text-[#e07a6c]" data-testid="rebook-failed-count">{rebookResults.failed}</p><p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mt-1">Failed</p></div>
              <div className="flex-1 border border-white/10 p-3 text-center"><p className="text-2xl font-bold text-gray-400" data-testid="rebook-skipped-count">{rebookResults.skipped}</p><p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mt-1">No email</p></div>
            </div>

            <div className="border border-white/10 divide-y divide-white/5 mb-5">
              {(rebookResults.results || []).map((r) => (
                <div key={r.id} data-testid={`rebook-result-${r.id}`} className="flex items-center gap-3 px-4 py-3">
                  {r.status === "sent" ? <CheckCircle2 size={16} className="text-emerald-400 shrink-0" /> : r.status === "failed" ? <XCircle size={16} className="text-[#e07a6c] shrink-0" /> : <MinusCircle size={16} className="text-gray-500 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">{r.name}</p>
                    <p className="text-xs text-gray-500 truncate">{r.email}{r.reason ? ` · ${r.reason}` : ""}</p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.15em] ${r.status === "sent" ? "text-emerald-400" : r.status === "failed" ? "text-[#e07a6c]" : "text-gray-500"}`}>{r.status === "skipped" ? "No email" : r.status}</span>
                </div>
              ))}
              {(rebookResults.results || []).length === 0 && <p className="px-4 py-6 text-center text-gray-600 text-xs uppercase tracking-[0.2em]">No Thursday reservations</p>}
            </div>

            {rebookResults.failed > 0 && (
              <button data-testid="resend-failed-btn" onClick={resendFailedRebook} disabled={sendingRebook} className="w-full uppercase tracking-[0.15em] text-xs font-bold py-3.5 bg-[#C44536] text-white hover:bg-[#d1543f] transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50"><Mail size={14} /> {sendingRebook ? "Resending…" : `Resend to ${rebookResults.failed} failed only`}</button>
            )}
          </div>
        </div>
      )}

      {quoteFor && (
        <div data-testid="quote-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setQuoteFor(null)}>
          <div className="bg-[#0C0C0E] border border-white/15 w-full max-w-md p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-1">Private Transfer Quote</p>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl">{quoteFor.customer.full_name}</h3>
                <p className="text-gray-500 text-xs mt-1">{quoteFor.tour_name} · {formatDate(quoteFor.date)} · {(quoteFor.adults || 0) + (quoteFor.children || 0)} guest(s)</p>
              </div>
              <button data-testid="quote-close" onClick={() => setQuoteFor(null)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>

            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Quote amount (ISK)</label>
            <input data-testid="quote-amount" type="number" min="0" value={quoteAmount} onChange={(e) => setQuoteAmount(e.target.value)} placeholder="e.g. 85000" className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-1" />
            {Number(quoteAmount) > 0 && <p className="text-[11px] text-gray-500 mb-4">= {formatISK(Number(quoteAmount))}</p>}

            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2 mt-3">Note to guest (optional)</label>
            <textarea data-testid="quote-note" value={quoteNote} onChange={(e) => setQuoteNote(e.target.value)} rows={3} placeholder="e.g. Private transfer for your group of 4, direct pickup from your hotel at 08:30." className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-5" />

            {quoteFor.private_transfer_quote_sent_at && <p className="text-[11px] text-amber-300/80 mb-4">A quote of {formatISK(quoteFor.private_transfer_quote)} was already sent. Sending again will update it.</p>}

            <button data-testid="quote-send-btn" onClick={sendQuote} disabled={quoteBusy} className="w-full uppercase tracking-[0.15em] text-xs font-bold py-3.5 bg-amber-400 text-black hover:bg-amber-300 transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50"><Mail size={14} /> {quoteBusy ? "Sending…" : "Email quote to guest"}</button>
            <button data-testid="quote-test-btn" onClick={sendQuoteTest} disabled={quoteBusy} className="w-full mt-3 uppercase tracking-[0.15em] text-xs font-bold py-3 border border-white/15 text-gray-300 hover:border-white/50 transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50"><Mail size={14} /> Send test to myself</button>
          </div>
        </div>
      )}

      {msgFor && (
        <div data-testid="message-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setMsgFor(null)}>
          <div className="bg-[#0C0C0E] border border-white/15 w-full max-w-md max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-sky-300 mb-1">Message Guest</p>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl">{msgFor.customer.full_name}</h3>
                <p className="text-gray-500 text-xs mt-1">{msgFor.tour_name || `${msgFor.seating_label || "Dinner"} ${msgFor.seating || ""}`.trim()} · {formatDate(msgFor.date)} · {msgFor.customer.email}</p>
              </div>
              <button data-testid="message-close" onClick={() => setMsgFor(null)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>

            {templates.length > 0 && (
              <div className="mb-5">
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Templates</label>
                <div className="flex flex-wrap gap-2">
                  {templates.map((t) => (
                    <span key={t.id} data-testid={`template-chip-${t.id}`} className="inline-flex items-center gap-1.5 text-xs border border-white/15 hover:border-sky-300/60 text-gray-300 pl-3 pr-1.5 py-1.5 transition-all">
                      <button onClick={() => { setMsgSubject(t.subject); setMsgBody(t.body); }} className="hover:text-sky-300">{t.name}</button>
                      <button onClick={() => deleteTemplate(t.id)} className="text-gray-600 hover:text-[#e07a6c]" title="Delete template"><X size={12} /></button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Subject</label>
            <input data-testid="message-subject" value={msgSubject} onChange={(e) => setMsgSubject(e.target.value)} placeholder="e.g. A change to your pickup time" className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-5" />

            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs uppercase tracking-[0.15em] text-gray-500">Message</label>
              <button data-testid="save-template-btn" onClick={() => saveTemplate(msgSubject, msgBody)} className="text-[11px] uppercase tracking-[0.12em] text-gray-500 hover:text-sky-300 inline-flex items-center gap-1"><Plus size={11} /> Save as template</button>
            </div>
            <textarea data-testid="message-body" value={msgBody} onChange={(e) => setMsgBody(e.target.value)} rows={6} placeholder="Write your message. Each email opens with “Dear [first name],” automatically. Tip: type {name} to insert the guest's first name anywhere." className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-1" />
            <p className="text-[10px] text-gray-600 mb-5">Each email greets the guest by name automatically. Use <span className="text-gray-400">{"{name}"}</span> to drop their first name anywhere in the text.</p>

            <button data-testid="message-send-btn" onClick={sendMessage} disabled={msgBusy} className="w-full uppercase tracking-[0.15em] text-xs font-bold py-3.5 bg-sky-400 text-black hover:bg-sky-300 transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50"><Mail size={14} /> {msgBusy ? "Sending…" : "Email message to guest"}</button>
            <button data-testid="message-test-btn" onClick={sendMessageTest} disabled={msgBusy} className="w-full mt-3 uppercase tracking-[0.15em] text-xs font-bold py-3 border border-white/15 text-gray-300 hover:border-white/50 transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50"><Mail size={14} /> Send test to myself</button>

            {(msgFor.messages || []).length > 0 && (
              <div className="mt-7 pt-5 border-t border-white/10">
                <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-3">Message history ({msgFor.messages.length})</p>
                <div className="space-y-3" data-testid="message-history">
                  {[...msgFor.messages].reverse().map((m) => (
                    <div key={m.id || m.sent_at} className="bg-black/40 border border-white/10 px-3.5 py-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-white font-semibold truncate">{m.subject}</p>
                        <span className="text-[10px] text-gray-500 whitespace-nowrap">{fmtDateTime(m.sent_at)}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1.5 whitespace-pre-line">{m.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {bulkOpen && (
        <div data-testid="bulk-message-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setBulkOpen(false)}>
          <div className="bg-[#0C0C0E] border border-white/15 w-full max-w-md max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-sky-300 mb-1">Bulk Message</p>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl">Email a whole {tab === "tours" ? "activity" : "sitting"}</h3>
                <p className="text-gray-500 text-xs mt-1">Sends one email to every {paidOnly ? "paid " : ""}guest in the group you pick.</p>
              </div>
              <button data-testid="bulk-close" onClick={() => setBulkOpen(false)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>

            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">{tab === "tours" ? "Activity" : "Dinner sitting"}</label>
            <select data-testid="bulk-group" value={bulkGroup} onChange={(e) => setBulkGroup(e.target.value)} className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-5">
              <option value="">Choose a group…</option>
              {bulkGroups().map((g) => <option key={g.key} value={g.key}>{g.label} · {g.count} booking{g.count !== 1 ? "s" : ""} · {g.pax} pax</option>)}
            </select>

            {templates.length > 0 && (
              <div className="mb-5">
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Templates</label>
                <div className="flex flex-wrap gap-2">
                  {templates.map((t) => (
                    <button key={t.id} data-testid={`bulk-template-${t.id}`} onClick={() => { setBulkSubject(t.subject); setBulkBody(t.body); }} className="text-xs border border-white/15 hover:border-sky-300/60 text-gray-300 hover:text-sky-300 px-3 py-1.5 transition-all">{t.name}</button>
                  ))}
                </div>
              </div>
            )}

            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Subject</label>
            <input data-testid="bulk-subject" value={bulkSubject} onChange={(e) => setBulkSubject(e.target.value)} placeholder="e.g. Weather update for tomorrow" className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-5" />

            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs uppercase tracking-[0.15em] text-gray-500">Message</label>
              <button data-testid="bulk-save-template-btn" onClick={() => saveTemplate(bulkSubject, bulkBody)} className="text-[11px] uppercase tracking-[0.12em] text-gray-500 hover:text-sky-300 inline-flex items-center gap-1"><Plus size={11} /> Save as template</button>
            </div>
            <textarea data-testid="bulk-body" value={bulkBody} onChange={(e) => setBulkBody(e.target.value)} rows={6} placeholder="Write your message. Each email opens with “Dear [first name],” automatically. Tip: type {name} to insert each guest's first name anywhere." className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-1" />
            <p className="text-[10px] text-gray-600 mb-5">Every recipient is greeted by their own name automatically. Use <span className="text-gray-400">{"{name}"}</span> to drop their first name anywhere in the text.</p>

            <button data-testid="bulk-send-btn" onClick={sendBulk} disabled={bulkBusy} className="w-full uppercase tracking-[0.15em] text-xs font-bold py-3.5 bg-sky-400 text-black hover:bg-sky-300 transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50"><Mail size={14} /> {bulkBusy ? "Sending…" : "Email everyone in this group"}</button>
          </div>
        </div>
      )}

      {reschedFor && (
        <div data-testid="reschedule-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setReschedFor(null)}>
          <div className="bg-[#0C0C0E] border border-white/15 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#c9a24b] mb-1">Ask to Reschedule</p>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl">{reschedFor.customer.full_name}</h3>
                <p className="text-gray-500 text-xs mt-1">{reschedFor.tour_name} · currently {formatDate(reschedFor.date)}</p>
              </div>
              <button data-testid="reschedule-close" onClick={() => setReschedFor(null)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>

            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Days to offer the client</label>
            <div className="flex flex-wrap gap-2 mb-5">
              {(cfg.dates || []).filter((d) => d !== reschedFor.date).map((d) => {
                const on = reschedDates.includes(d);
                return (
                  <button key={d} data-testid={`resched-date-${d}`} onClick={() => toggleReschedDate(d)} className={`px-4 py-2.5 text-sm border transition-all ${on ? "border-[#c9a24b] bg-[#c9a24b]/10 text-white" : "border-white/15 text-gray-400 hover:border-white/40"}`}>{formatDate(d)}</button>
                );
              })}
            </div>

            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Message (optional — leave blank for the default reason)</label>
            <textarea data-testid="reschedule-note" value={reschedNote} onChange={(e) => setReschedNote(e.target.value)} rows={3} placeholder="Because our excursions are quite a distance from the city, we group guests together for transfers…" className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-5" />

            <div className="flex items-center gap-3 mb-5">
              <button data-testid="reschedule-preview-btn" onClick={previewReschedule} disabled={reschedBusy} className="uppercase tracking-[0.12em] text-xs font-bold py-3 px-5 border border-white/20 text-white hover:border-white/50 transition-all inline-flex items-center gap-2 disabled:opacity-50"><Eye size={14} /> Preview email</button>
              <button data-testid="reschedule-test-btn" onClick={sendReschedTest} disabled={reschedBusy} className="uppercase tracking-[0.12em] text-xs font-bold py-3 px-5 border border-white/15 text-gray-300 hover:border-white/50 transition-all inline-flex items-center gap-2 disabled:opacity-50"><Mail size={14} /> Test to myself</button>
              <button data-testid="reschedule-send-btn" onClick={sendReschedule} disabled={reschedBusy} className="uppercase tracking-[0.12em] text-xs font-bold py-3 px-5 bg-[#c9a24b] text-black hover:bg-[#d9b45f] transition-all inline-flex items-center gap-2 disabled:opacity-50"><Mail size={14} /> {reschedBusy ? "Working…" : "Send to client"}</button>
            </div>

            {reschedPreview ? (
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Email preview</p>
                <iframe data-testid="reschedule-preview-frame" title="Email preview" srcDoc={reschedPreview} className="w-full h-[520px] border border-white/10 bg-white rounded" />
              </div>
            ) : (
              <p className="text-gray-600 text-xs">Tip: click <strong className="text-gray-400">Preview email</strong> to see exactly what the client will receive before sending.</p>
            )}
          </div>
        </div>
      )}

      {carReschedFor && (
        <div data-testid="car-reschedule-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setCarReschedFor(null)}>
          <div className="bg-[#0C0C0E] border border-white/15 w-full max-w-lg max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#c9a24b] mb-1">Reschedule Whole Car</p>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl">{carReschedFor.car.label}</h3>
                <p className="text-gray-500 text-xs mt-1">{formatDate(carReschedFor.car.date)} · emails {carReschedFor.members.length} guest(s) in this car</p>
              </div>
              <button data-testid="car-reschedule-close" onClick={() => setCarReschedFor(null)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>

            <div className="mb-5 border border-white/10 bg-black/40 divide-y divide-white/5">
              {carReschedFor.members.map((b) => (
                <div key={b.id} className="flex items-center justify-between px-3.5 py-2 text-sm">
                  <span className="text-white truncate">{b.customer?.full_name || "—"}</span>
                  <span className="text-gray-500 text-xs truncate ml-3">{b.tour_name}</span>
                </div>
              ))}
            </div>

            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Days to offer everyone</label>
            <div className="flex flex-wrap gap-2 mb-5">
              {(cfg.dates || []).filter((d) => d !== carReschedFor.car.date).map((d) => {
                const on = carReschedDates.includes(d);
                return (
                  <button key={d} data-testid={`car-resched-date-${d}`} onClick={() => toggleCarReschedDate(d)} className={`px-4 py-2.5 text-sm border transition-all ${on ? "border-[#c9a24b] bg-[#c9a24b]/10 text-white" : "border-white/15 text-gray-400 hover:border-white/40"}`}>{formatDate(d)}</button>
                );
              })}
            </div>

            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Message (optional — leave blank for the default reason)</label>
            <textarea data-testid="car-reschedule-note" value={carReschedNote} onChange={(e) => setCarReschedNote(e.target.value)} rows={3} placeholder="Because your transfer has changed, we'd like to move your excursion to another day…" className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-5" />

            <button data-testid="car-reschedule-send-btn" onClick={sendCarReschedule} disabled={carReschedBusy} className="w-full uppercase tracking-[0.12em] text-xs font-bold py-3.5 bg-[#c9a24b] text-black hover:bg-[#d9b45f] transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50"><Mail size={14} /> {carReschedBusy ? "Sending…" : `Email all ${carReschedFor.members.length} guest(s)`}</button>
          </div>
        </div>
      )}


      {editing && (
        <div data-testid="edit-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="bg-[#0C0C0E] border border-white/15 w-full max-w-md p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Edit Booking</p>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl">{editing.customer.full_name}</h3>
                <p className="text-gray-500 text-xs mt-1">{editing.type === "dinner" ? "Fine Dining" : editing.tour_name}</p>
              </div>
              <button data-testid="edit-close" onClick={() => setEditing(null)} className="text-gray-500 hover:text-white"><X size={18} /></button>
            </div>

            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Date</label>
            <select data-testid="edit-date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-5">
              {cfg.dates.map((d) => <option key={d} value={d}>{formatDate(d)}</option>)}
            </select>

            {editing.type === "buggy" && (
              <>
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Time slot</label>
                <select data-testid="edit-slot" value={editForm.slot} onChange={(e) => setEditForm({ ...editForm, slot: e.target.value })} className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-5">
                  {(cfg.buggy_slots || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Riders (pax)</label>
                <div className="flex items-center justify-between py-2.5 border-b border-white/10 mb-3">
                  <span className="text-sm text-gray-400">Guests riding</span>
                  <div className="flex items-center gap-4">
                    <button type="button" data-testid="edit-adults-minus" onClick={() => setEditGuest("adults", -1)} disabled={editForm.adults <= 0} className="w-8 h-8 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors disabled:opacity-30 text-white"><Minus size={14} /></button>
                    <span data-testid="edit-adults-value" className="w-5 text-center font-bold">{editForm.adults}</span>
                    <button type="button" data-testid="edit-adults-plus" onClick={() => setEditGuest("adults", 1)} className="w-8 h-8 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors text-white"><Plus size={14} /></button>
                  </div>
                </div>
                <p className="text-[11px] text-gray-500 mb-5 leading-relaxed">Buggy pricing is charged per buggy, so changing rider count won't change the amount already paid.</p>
              </>
            )}
            {editing.type === "dinner" && (
              <>
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Seating</label>
                <select data-testid="edit-seating" value={editForm.seating} onChange={(e) => setEditForm({ ...editForm, seating: e.target.value })} className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-5">
                  {seatingsForDate(editForm.date || editing.date).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Journey choice <span className="text-gray-600 normal-case tracking-normal">· must total {editing.party_size} guest{editing.party_size > 1 ? "s" : ""}</span></label>
                <div className="space-y-1 mb-2" data-testid="edit-journeys">
                  {["Fire", "Ocean", "Earth"].map((k) => (
                    <div key={k} className="flex items-center justify-between py-2.5 border-b border-white/10">
                      <span className="text-sm">{k}</span>
                      <div className="flex items-center gap-4">
                        <button type="button" data-testid={`edit-journey-${k.toLowerCase()}-minus`} onClick={() => setEditJourney(k, -1)} disabled={editForm.journeys[k] <= 0} className="w-8 h-8 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors disabled:opacity-30 text-white">−</button>
                        <span data-testid={`edit-journey-${k.toLowerCase()}-value`} className="w-5 text-center font-bold">{editForm.journeys[k]}</span>
                        <button type="button" data-testid={`edit-journey-${k.toLowerCase()}-plus`} onClick={() => setEditJourney(k, 1)} className="w-8 h-8 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors text-white">+</button>
                      </div>
                    </div>
                  ))}
                </div>
                {(() => {
                  const tot = editForm.journeys.Fire + editForm.journeys.Ocean + editForm.journeys.Earth;
                  return <p className={`text-xs mb-5 ${tot === editing.party_size ? "text-gray-500" : "text-[#e07a6c]"}`} data-testid="edit-journey-total">{tot} of {editing.party_size} assigned</p>;
                })()}
              </>
            )}
            {editing.type === "tour" && (
              <>
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Time</label>
                <input data-testid="edit-tour-time" value={editForm.slot} onChange={(e) => setEditForm({ ...editForm, slot: e.target.value })} placeholder="e.g. 09:00" className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-5" />
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Guests (pax)</label>
                <div className="flex items-center justify-between py-2.5 border-b border-white/10">
                  <span className="text-sm">Adults</span>
                  <div className="flex items-center gap-4">
                    <button type="button" data-testid="edit-adults-minus" onClick={() => setEditGuest("adults", -1)} disabled={editForm.adults <= 0} className="w-8 h-8 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors disabled:opacity-30 text-white"><Minus size={14} /></button>
                    <span data-testid="edit-adults-value" className="w-5 text-center font-bold">{editForm.adults}</span>
                    <button type="button" data-testid="edit-adults-plus" onClick={() => setEditGuest("adults", 1)} className="w-8 h-8 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors text-white"><Plus size={14} /></button>
                  </div>
                </div>
                {((editing.child_price || 0) > 0 || (editing.children || 0) > 0) && (
                  <div className="flex items-center justify-between py-2.5 border-b border-white/10">
                    <span className="text-sm">Children</span>
                    <div className="flex items-center gap-4">
                      <button type="button" data-testid="edit-children-minus" onClick={() => setEditGuest("children", -1)} disabled={editForm.children <= 0} className="w-8 h-8 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors disabled:opacity-30 text-white"><Minus size={14} /></button>
                      <span data-testid="edit-children-value" className="w-5 text-center font-bold">{editForm.children}</span>
                      <button type="button" data-testid="edit-children-plus" onClick={() => setEditGuest("children", 1)} className="w-8 h-8 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors text-white"><Plus size={14} /></button>
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-gray-500 mt-3 mb-5 leading-relaxed">Reducing guests (e.g. one person cancels) updates the total &amp; operator commission. Any card refund is handled separately in Stripe.</p>
              </>
            )}

            <div className="flex gap-3 mt-2">
              <button data-testid="edit-save" onClick={saveEdit} disabled={savingEdit} className="flex-1 bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-3.5 transition-colors disabled:opacity-60">
                {savingEdit ? "Saving…" : "Save changes"}
              </button>
            </div>
            {(editing.type === "tour" || editing.type === "dinner") && editing.payment_status === "paid" && (
              <button data-testid="edit-refund-cancel" onClick={() => { const b = editing; setEditing(null); refundCancelBooking(b); }} className="w-full mt-3 border border-[#C44536]/50 text-[#e07a6c] hover:bg-[#C44536]/10 uppercase tracking-[0.15em] text-xs font-bold py-3 transition-colors inline-flex items-center justify-center gap-2">
                <BadgeDollarSign size={14} /> Mark refunded &amp; cancelled
              </button>
            )}
            <p className="text-[11px] text-gray-500 mt-4 leading-relaxed">This changes the booking directly — the guest is <span className="text-gray-300">not notified</span>. To tell them, use the ✉️ or reschedule button on the booking row afterwards.</p>
          </div>
        </div>
      )}

      {addDinner && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="add-dinner-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setAddDinner(null)} />
          <div className="relative bg-[#0C0C0E] border border-white/15 w-full max-w-md p-8 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setAddDinner(null)} className="absolute top-5 right-5 text-gray-500 hover:text-white"><X size={18} /></button>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Add Dinner Booking</p>
            <h3 className="font-display font-bold text-2xl mb-1">New reservation</h3>
            <p className="text-[11px] text-gray-500 mb-6">Created as UNPAID — for guests you're adding manually.</p>

            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Guest name *</label>
            <input data-testid="add-dinner-name" value={addDinner.full_name} onChange={(e) => setAddDinner({ ...addDinner, full_name: e.target.value })} placeholder="Full name" className="w-full bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40 mb-4" />
            <div className="grid grid-cols-2 gap-3 mb-4">
              <input data-testid="add-dinner-email" value={addDinner.email} onChange={(e) => setAddDinner({ ...addDinner, email: e.target.value })} placeholder="Email (optional)" className="bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40" />
              <input data-testid="add-dinner-phone" value={addDinner.phone} onChange={(e) => setAddDinner({ ...addDinner, phone: e.target.value })} placeholder="Phone (optional)" className="bg-black border border-white/15 px-4 py-3 text-sm text-white focus:outline-none focus:border-white/40" />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Date</label>
                <select data-testid="add-dinner-date" value={addDinner.date} onChange={(e) => setAddDinner({ ...addDinner, date: e.target.value })} className="w-full bg-black border border-white/15 px-3 py-3 text-sm text-white focus:outline-none focus:border-white/40">
                  {(cfg.dates || []).map((d) => <option key={d} value={d}>{formatDate(d)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Sitting</label>
                <select data-testid="add-dinner-seating" value={addDinner.seating} onChange={(e) => setAddDinner({ ...addDinner, seating: e.target.value })} className="w-full bg-black border border-white/15 px-3 py-3 text-sm text-white focus:outline-none focus:border-white/40">
                  {seatingsForDate(addDinner.date).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <label className="block text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Journeys</label>
            <div className="space-y-1 mb-2" data-testid="add-dinner-journeys">
              {["Fire", "Ocean", "Earth"].map((k) => (
                <div key={k} className="flex items-center justify-between py-2.5 border-b border-white/10">
                  <span className="text-sm">{k}</span>
                  <div className="flex items-center gap-4">
                    <button type="button" data-testid={`add-journey-${k.toLowerCase()}-minus`} onClick={() => setAddJourney(k, -1)} disabled={addDinner.journeys[k] <= 0} className="w-8 h-8 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors disabled:opacity-30 text-white"><Minus size={14} /></button>
                    <span data-testid={`add-journey-${k.toLowerCase()}-value`} className="w-5 text-center font-bold">{addDinner.journeys[k]}</span>
                    <button type="button" data-testid={`add-journey-${k.toLowerCase()}-plus`} onClick={() => setAddJourney(k, 1)} className="w-8 h-8 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors text-white"><Plus size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between py-2.5 border-b border-white/10 mb-2">
              <span className="text-sm">Beverage pairing</span>
              <div className="flex items-center gap-4">
                <button type="button" data-testid="add-beverage-minus" onClick={() => setAddDinner((f) => ({ ...f, beverage_count: Math.max(0, f.beverage_count - 1) }))} disabled={addDinner.beverage_count <= 0} className="w-8 h-8 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors disabled:opacity-30 text-white"><Minus size={14} /></button>
                <span data-testid="add-beverage-value" className="w-5 text-center font-bold">{addDinner.beverage_count}</span>
                <button type="button" data-testid="add-beverage-plus" onClick={() => setAddDinner((f) => ({ ...f, beverage_count: f.beverage_count + 1 }))} className="w-8 h-8 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors text-white"><Plus size={14} /></button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-4" data-testid="add-dinner-party">Party size: {addDinner.journeys.Fire + addDinner.journeys.Ocean + addDinner.journeys.Earth} guest(s)</p>

            <div className="mb-6">
              <p className="text-[11px] uppercase tracking-[0.15em] text-gray-500 mb-2">Price</p>
              <div className="grid grid-cols-3 gap-2">
                {[["standard", "Standard"], ["comp", "Comp · Free"], ["custom", "Custom"]].map(([m, lbl]) => (
                  <button key={m} type="button" data-testid={`add-dinner-price-${m}`} onClick={() => setAddDinner((f) => ({ ...f, price_mode: m }))}
                    className={`py-2.5 text-[11px] uppercase tracking-[0.1em] font-bold border transition-all ${(addDinner.price_mode || "standard") === m ? "bg-white text-black border-white" : "border-white/15 text-gray-400 hover:border-white/40"}`}>{lbl}</button>
                ))}
              </div>
              {addDinner.price_mode === "comp" && <p className="text-[11px] text-sky-400/80 mt-2">Free comp booking — 0 ISK, marked as Comp.</p>}
              {addDinner.price_mode === "custom" && (
                <input type="number" min="0" data-testid="add-dinner-custom-total" value={addDinner.custom_total} onChange={(e) => setAddDinner((f) => ({ ...f, custom_total: e.target.value }))} placeholder="Total ISK charged"
                  className="mt-2 w-full bg-black border border-white/15 px-4 py-2.5 text-sm text-white focus:outline-none focus:border-white/40" />
              )}
              {(!addDinner.price_mode || addDinner.price_mode === "standard") && <p className="text-[11px] text-gray-500 mt-2">Standard dinner price, recorded as unpaid (guest still owes).</p>}
            </div>

            <label className="flex items-center gap-3 mb-6 cursor-pointer select-none" data-testid="add-dinner-send-confirmation-label">
              <input type="checkbox" data-testid="add-dinner-send-confirmation" checked={!!addDinner.send_confirmation} onChange={(e) => setAddDinner((f) => ({ ...f, send_confirmation: e.target.checked }))} className="w-4 h-4 accent-white" />
              <span className="text-sm text-gray-300">Email the guest their confirmation + QR pass{addDinner.email ? "" : " (add an email above first)"}</span>
            </label>

            <button data-testid="add-dinner-save" onClick={saveAddDinner} disabled={savingAdd} className="w-full bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-3.5 transition-colors disabled:opacity-60">
              {savingAdd ? "Adding…" : addDinner.price_mode === "comp" ? "Add comp booking (0 ISK)" : addDinner.price_mode === "custom" ? "Add paid booking" : "Add unpaid booking"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
